/**
 * **Arena run core**: the run state + the pure, seeded, clock-free wave/elite/
 * boss choreography of the horde-survival minigame (Arena, PRD #334, Slice B).
 *
 * This is the arena's sibling to {@link ./storm} and {@link ./ratSwarm}: an
 * ephemeral {@link ArenaRunState} lives beside the run's own private, seeded
 * {@link World} (never persisted, never snapshotted), and a tick-driven
 * `stepArena` advances the whole schedule - escalating waves off the perimeter
 * spawn ring, interval elites, the boss milestone, the run clock - returning
 * {@link ArenaBeat}s for the server to broadcast (the `stepStorm`/`StormBeat`
 * shape exactly). The horde's chase-and-bite lives in {@link ./horde} (the
 * vortex-pursuit sibling) and is stepped from inside `stepArena`, so pausing the
 * run for a draft freezes *everything* - clock, spawns, and pursuit - with one
 * status flag.
 *
 * All randomness (spawn-ring picks, facings, bite damage/clips) flows through
 * the run world's seeded `world.rng`, and nothing reads a wall clock, so the
 * same seed replays the identical run beat-for-beat (the architecture test's
 * contract) and the whole thing unit-tests in node. The **acts** - constructing
 * the run world, routing its snapshots to the owning socket, broadcasting the
 * beats/effects, despawning the reaped dead, payout and teardown - are server
 * I/O for the Arena server slice, exactly as `gameServer.ts` enacts the storm's.
 *
 * **Slice C (ADR-0175, reworked by ADR-0179) lives here too:** the build loop -
 * the fire mage's one base skill volleys itself on its cooldown from inside
 * {@link stepArena} (movement stays the only manual input) and the drafted
 * POE-style modifiers cascade each volley ({@link fireSkill}), each reaped kill
 * drops its XP as a ground gem the runner magnetizes up ({@link ./xpGem} -
 * XP lands at pickup, VS-style), a **seeded, deterministic** level-up draft
 * flips the run to `paused-draft` and grows the build ({@link rollArenaDraft} /
 * {@link applyArenaPick}), and the pure score/payout functions
 * ({@link arenaScore} / {@link arenaPayout}) the server slice banks against.
 * The tuning tables and their pure math live in {@link ./arenaWeapons}; damage
 * and death flow only through the shared {@link ./combat} seam. One caveat for
 * the server slice (#338): the `paused-draft` freeze covers everything *this
 * module* drives - the run's `world.tick` is the server's call, and it must be
 * **skipped too** while `paused-draft` holds, or already-issued routes glide
 * out and the runner regens under the pause.
 */
import type { Entity, EntityId } from './entity';
import type { World } from './world';
import { ALL_DIRECTIONS, Direction, deltaForDirection, directionForDelta } from './direction';
import { ENERGY_VORTEX_BODY, monsterById } from './content';
import { applyDamage, isDead } from './combat';
import { Mobile } from './mobile';
import { walkableForLevel } from './walkability';
import { createPlayer, type PlayerOptions } from './player';
import {
  ARENA_BURNING_WINDOW_TICKS,
  ARENA_CATACLYSM_BURN_RADIUS,
  ARENA_CATACLYSM_BURN_TICKS,
  ARENA_CHAIN_RANGE,
  ARENA_MAX_CASCADE_EVENTS,
  ARENA_REGEN_HP_PER_STAT_LEVEL,
  ARENA_REGEN_INTERVAL_TICKS,
  ARENA_MAX_PENDING_METEORS,
  ARENA_METEOR_DELAY_TICKS,
  ARENA_METEOR_POOL_RADIUS,
  ARENA_METEOR_POOL_TICKS,
  ARENA_METEOR_RADIUS,
  ARENA_ASCENSION_IDS,
  ARENA_MODIFIER_IDS,
  ARENA_MODIFIER_MAX_LEVEL,
  ARENA_STAT_IDS,
  ARENA_STAT_MAX_LEVEL,
  ARENA_ZONE_BURN_INTERVAL_TICKS,
  arenaMoveMsPerTile,
  arenaCastSettleTicks,
  arenaXpGain,
  arenaXpToNext,
  cataclysmBurnDamage,
  combustionDamage,
  combustionRadius,
  chainHopDamage,
  forkBranchDamage,
  chainHops,
  explosionDamage,
  explosionRadius,
  flameStrikeChance,
  flameStrikeDamage,
  cometKnockbackTiles,
  cometLanding,
  flameStrikeRadius,
  isOnPierceRay,
  meteorDamage,
  meteorPoolDamage,
  novaInterval,
  novaRange,
  novaRayDamage,
  pierceRayEnd,
  scorchedTrailDamage,
  scorchedTrailDurationTicks,
  scorchedTrailWidth,
  skillCooldownTicks,
  skillProjectileCount,
  skillProjectileDamage,
  skillProjectileKind,
  skillProjectileSizeTier,
  skillRange,
  splinterDamage,
  splinterRange,
  type ArenaAscensionId,
  type ArenaAscensions,
  type ArenaModifierId,
  type ArenaModifierLevels,
  type ArenaProjectileKind,
  type ArenaStatId,
} from './arenaWeapons';
import { getArenaClass, DEFAULT_ARENA_CLASS_ID, type ArenaClassDef, type ArenaClassId } from './arenaClasses';
import {
  ARENA_CHALLENGES,
  DEFAULT_ARENA_CHALLENGE_ID,
  captureArenaChallenge,
  type ArenaChallengeDefinition,
  type ArenaChallengeRules,
} from './arenaChallenges';
import {
  ARCHER_BLEED_INTERVAL_TICKS,
  ARCHER_BLEED_MAX_STACKS,
  ARCHER_CALTROP_SLOW_DURATION_TICKS,
  ARCHER_MAX_CALTROPS,
  ARCHER_RICOCHET_RANGE,
  archerArrowCount,
  archerArrowDamage,
  archerCritChance,
  archerCritDamage,
  archerProjectileKind,
  archerProjectileSizeTier,
  archerRicochetBounces,
  archerRicochetDamage,
  bleedDurationTicks,
  bleedTickDamage,
  caltropSlowFactor,
  caltropTileDurationTicks,
  poisonCloudDamage,
  poisonCloudDurationTicks,
  poisonCloudRadius,
} from './arenaClasses/archer';
import type { ArenaMap } from './arenaMap';
import type { SpawnPoint } from './authoredMap';
import {
  createHordePursuit,
  spawnHordeEnemy,
  stepHordePursuit,
  type HordeEffect,
  type HordePursuitState,
} from './horde';
import {
  createVortexPursuit,
  stepVortexPursuit,
  type VortexPursuitState,
} from './vortex';
import { stepProjectiles, type ProjectileHit } from './projectile';
import {
  ARENA_VACUUM_ELITE_CHANCE,
  ARENA_VACUUM_FIELD_CAP,
  ARENA_XP_GEM_CAP,
  arenaMagnetRadius,
  collectVacuums,
  collectXpGems,
  spawnVacuum,
  spawnXpGem,
} from './xpGem';

// ---------------------------------------------------------------------------
// Tunables. Every knob of a run, in one place (PRD #334 starting values). All
// spans are in **ticks** (the 50ms fixed step, 20 ticks = 1s) - the sim is
// clock-free, so every cadence falls out of counting, the storm's discipline.
// The wave curve / HP / damage numbers are explicit tuning surfaces; keep them
// named and balanced against the ~{@link ARENA_ENEMY_CAP} concurrent target.
// ---------------------------------------------------------------------------

/**
 * The arena is **endless** (ADR-0210): there is no survival horn and no run
 * length - a run only ends when the runner falls, and the goal is to push the
 * {@link arenaScore score} as high as possible for the leaderboard (ADR-0172).
 * The horde escalates forever ({@link arenaStageFor}'s endless tiers) so there
 * is always a rising wall. This span is kept only as the **par time** the
 * time-based half of the score/payout reads against (~10 minutes at 20Hz), NOT
 * a cap: `elapsedTicks` runs past it freely.
 */
export const ARENA_PAR_TICKS = 12000;

/**
 * Hard cap on concurrently-live enemies **per run** (the best case, a lone
 * runner). Raised 100 -> 150 with the bestiary retune (ADR-0184): the
 * multi-projectile/chain/explosion build is fed by a genuinely packed field.
 * Still a single-client delta stream. A run's *actual* cap is captured at run
 * start by the concurrency governor ({@link arenaEnemyBudget}, PRD #387 Slice
 * D) and never exceeds this.
 */
export const ARENA_ENEMY_CAP = 150;

/**
 * The **global enemy budget** across every concurrent run (PRD #387 Slice D) -
 * the single server-load knob now that per-enemy cost is O(1) sign-stepping
 * (ADR-0205) and world size contributes nothing (ADR-0206). Sized to give
 * *every* run the full {@link ARENA_ENEMY_CAP} across the whole legal
 * concurrency range (16 runs = the `ARENA_RUNS_MAX` admin ceiling): the enemy
 * cap was silently doubling as a **score handicap** - a run that started while
 * the arena was quiet locked in 150 monsters and out-farmed a run that started
 * crowded (60), so the leaderboard compared structurally unequal fields.
 * Guaranteeing 150 for every run makes scores apples-to-apples by construction.
 * The adaptive split ({@link arenaEnemyBudget}) stays intact purely as a
 * safety valve should `ARENA_RUNS_MAX` ever rise past what this budget covers.
 */
export const ARENA_GLOBAL_ENEMY_BUDGET = ARENA_ENEMY_CAP * 16;

/**
 * The least a run's enemy cap can be squeezed to under load
 * ({@link arenaEnemyBudget}'s floor): recycling keeps whatever budget a run
 * gets fully concentrated around its runner, so even the floor plays as
 * constant pressure - just a thinner horde.
 */
export const ARENA_ENEMY_CAP_FLOOR = 50;

/**
 * The adaptive per-run enemy cap for a run starting while `activeRuns` runs
 * (including itself) are live: an even share of the global budget, clamped to
 * `[floor, cap]`. Captured **once at run start** into
 * {@link ArenaRunState.enemyCap} (the StormTuning capture pattern) - a run's
 * budget never changes mid-run; the next run picks up the new count. Pure;
 * the server passes the live run count.
 */
export function arenaEnemyBudget(
  activeRuns: number,
  globalBudget = ARENA_GLOBAL_ENEMY_BUDGET,
): number {
  const share = Math.floor(globalBudget / Math.max(1, activeRuns));
  return Math.min(ARENA_ENEMY_CAP, Math.max(ARENA_ENEMY_CAP_FLOOR, share));
}

/**
 * Slots held back from waves/elites until the boss has spawned, so the
 * {@link ARENA_BOSS_TICKS} milestone can never be starved by a saturated field
 * - the boss always makes its entrance, even in a run where nothing has died.
 */
export const ARENA_BOSS_RESERVE = 1;

/**
 * The runner's per-tile gait: the **town walk gait** (250ms, `mobile.ts`),
 * giving the UO-standard 250/125 walk/run pair (the run gait derives as half
 * the walk). The original PRD spawned the runner at double speed (125/62.5,
 * "a walking default would feel sluggish") but live play showed the horde
 * could never threaten a runner that fast - the stop-to-cast retune
 * (ADR-0191) slows the runner to town speed so kiting is a real trade
 * against {@link ARENA_CAST_SETTLE_TICKS casting}.
 */
export const ARENA_PLAYER_MS_PER_TILE = 250;

/**
 * Stop-to-cast (ADR-0191): the skill only volleys once the runner has held
 * the same tile for this many consecutive ticks - moving and firing are
 * mutually exclusive, so kiting costs damage. Stillness is tracked by
 * watching the runner's tile across {@link stepArena} ticks (NOT the mobile's
 * step state: a predicted client's accepted walks land server-side as
 * instant teleports, so the run-world mobile never interpolates). The window
 * must exceed the walk-step interval (250ms = 5 ticks) or the counter would
 * hit the threshold between the steps of a continuously-walking runner; 6
 * ticks (300ms, ADR-0204's snappier plant - down from ADR-0191's 400ms) is
 * the shortest window that clears it, with a single tick of margin for
 * walk-request jitter - it cannot go lower without breaking the mechanic. A
 * held shot stays consumed-and-ready (the no-target hold), so the first
 * settled tick with a target in reach fires a full volley.
 */
export const ARENA_CAST_SETTLE_TICKS = 6;

// ---------------------------------------------------------------------------
// The player-centered spawn ring + enemy recycling (PRD #387 Slice A). The
// spawn/recycle radius pair is the anti-kiting mechanism that will replace the
// wall (Slice C): tune these two and the front-arc weight before touching
// gaits or wave sizes if kiting ever feels too safe or too punishing.
// ---------------------------------------------------------------------------

/**
 * Chebyshev radius of the ring waves/elites/boss materialize on around the
 * *runner* (not the map - PRD #387): just outside the viewport at default
 * zoom, so an entrance reads as fresh off-screen pressure wherever the runner
 * stands and pressure is constant by construction.
 */
export const ARENA_SPAWN_RADIUS = 22;

/**
 * Beyond this Chebyshev distance from the runner an outrun enemy is
 * **recycled**: teleported - same entity, same remaining health - onto the
 * spawn ring ahead of the runner's heading (~1.5x the spawn radius, so an
 * enemy is well off-screen before it jumps and the reappearance is
 * indistinguishable from a fresh spawn). The boss is exempt.
 */
export const ARENA_RECYCLE_RADIUS = 33;

/**
 * Chance a ring spawn draws its angle from the front arc around the runner's
 * heading instead of the full circle - heading-biased but never exclusively
 * frontal (PRD #387: running into danger is the default experience, yet the
 * horde still closes from everywhere).
 */
export const ARENA_SPAWN_FRONT_WEIGHT = 0.6;

/** Half-angle (radians) of the front arc around the runner's heading: +-90
 * degrees, i.e. the front arc IS the runner's forward half-plane. */
export const ARENA_SPAWN_FRONT_ARC = Math.PI / 2;

/**
 * The boss's gait multiplier while it has fallen beyond the recycle radius
 * (PRD #387 Slice C): it never recycles - the climax fight must feel earned -
 * so past the radius it closes at double speed (140ms -> 70ms per tile,
 * faster than the runner's 125ms run gait) until it is back inside, then
 * resumes its normal menace. Kiteable, never losable.
 */
export const ARENA_BOSS_CATCHUP_FACTOR = 0.5;

/** Ticks between regular waves - 5 seconds. */
export const ARENA_WAVE_INTERVAL_TICKS = 100;

/** Ticks between elite spawns - 30 seconds, a recurring spike of tension between waves. */
export const ARENA_ELITE_INTERVAL_TICKS = 600;

/**
 * Absolute tick the **first** elite makes its entrance: t+45s. Held longer than
 * the recurring {@link ARENA_ELITE_INTERVAL_TICKS} so the opening gives the
 * runner room to bank a couple of early level-ups before the first spike - the
 * boss has the same first-vs-recurring split ({@link ARENA_BOSS_TICKS}).
 */
export const ARENA_FIRST_ELITE_TICKS = 900;

/** Absolute tick the **first** boss makes its entrance: t+8:20, the first climax spike. */
export const ARENA_BOSS_TICKS = 10000;

/**
 * Ticks between recurring boss milestones (ADR-0210): after the first boss at
 * {@link ARENA_BOSS_TICKS}, a fresh (stat-escalated) boss enters every 5 minutes
 * - but only ever one at a time (a new one waits for the current to fall), so an
 * endless run is punctuated by memorable climax spikes rather than a boss pile-up.
 */
export const ARENA_BOSS_INTERVAL_TICKS = 6000;

/**
 * Per-milestone stat growth for the recurring boss (ADR-0210): boss N (0-indexed)
 * has `health x HEALTH_GROWTH^N` and `damage x DAMAGE_GROWTH^N`. Its gait is left
 * flat - a boss is a wall of HP, not a speedster; the {@link ARENA_BOSS_CATCHUP_FACTOR}
 * already makes it relentless.
 */
export const ARENA_BOSS_HEALTH_GROWTH = 1.6;
export const ARENA_BOSS_DAMAGE_GROWTH = 1.2;

/**
 * Endless escalation past the last defined {@link ARENA_STAGES} tier (ADR-0210):
 * every {@link ARENA_ENDLESS_STAGE_INTERVAL_TICKS} beyond the last tier's
 * `startTick`, the horde takes another step up - HP and XP by a geometric growth,
 * damage more gently, the wave a couple bigger, the gait a touch faster (floored).
 * Keeps a rising wall forever so a run is bounded only by the runner's skill.
 */
export const ARENA_ENDLESS_STAGE_INTERVAL_TICKS = 2000;
export const ARENA_ENDLESS_HEALTH_GROWTH = 1.35;
export const ARENA_ENDLESS_DAMAGE_GROWTH = 1.18;
export const ARENA_ENDLESS_XP_GROWTH = 1.35;
export const ARENA_ENDLESS_WAVESIZE_STEP = 2;
export const ARENA_ENDLESS_GAIT_STEP = 4;
export const ARENA_ENDLESS_MIN_MS_PER_TILE = 130;

/**
 * One roster tier of the escalating horde: from `startTick` on, regular waves
 * spawn `waveSize` enemies of this body/stat block. The tiering structure is
 * the point (PRD #334); the specific numbers are tuning surfaces.
 */
export interface ArenaStage {
  /** Elapsed tick this tier takes over (tiers are listed in ascending order). */
  startTick: number;
  /** The bestiary monster ids this tier's waves draw from (seeded pick per spawn). */
  monsters: readonly string[];
  /** Per-enemy full health for this tier. */
  health: number;
  /** Inclusive contact-damage roll range for this tier. */
  damageMin: number;
  damageMax: number;
  /** Per-tile gait (ms) - later tiers close faster. */
  msPerTile: number;
  /** Enemies per regular wave in this tier. */
  waveSize: number;
  /** XP one kill of this tier grants (Slice C - before the `xp-gain` stat). */
  xp: number;
}

/**
 * The time-tiered roster (bestiary phase 1, ADR-0184; expanded to eight tiers,
 * ADR-0211), each drawing its waves from a themed slice of the
 * {@link ../sim/content.monsters bestiary} - critters, beasts, the restless dead,
 * the warbands, the brutes, the dark casters, elementals & terathans, the
 * endgame. Past the last tier {@link arenaStageFor} cycles the whole roster at
 * escalating stats (ADR-0211). Each spawn picks
 * seeded-uniform from the tier's list (visual variety; stats are uniform
 * within a tier - per-monster stat flavor is a later knob). Wave sizes are
 * deliberately big: the multi-projectile/chain/explosion build wants a packed
 * field, and the horde outpaces a never-drafting runner **from the opening
 * wave** (kiting + drafting are mandatory - the ADR-0184 pressure retune; the
 * old "bare skill exactly clears stage 0" invariant is relaxed to "one-shots
 * tier-1 flesh and clears at least half the opening spawn rate"). **Tier 1
 * is pinned at 8** - the arenaWeapons balance guard holds the bare skill to
 * >= half the opening spawn rate, and 8 sits exactly on that floor; raise it
 * and the first draft stops being reachable. Sizes and health are the
 * ADR-0184 values: the interim ~3x wave flood (ADR-0189) and 2x tier 3-6 HP
 * (ADR-0191) compensated for a runner that could kite at double gait while
 * firing continuously, and stop-to-cast + the town gait (ADR-0191) made both
 * multipliers overshoot together, so ADR-0204 dials the pair back to the
 * pre-flood curve. Elites and the boss are separate
 * ({@link ARENA_ELITE}/{@link ARENA_BOSS}).
 */
export const ARENA_STAGES: readonly ArenaStage[] = [
  // Per-tier `xp` is tuned INVERSELY to `waveSize` (the cadence retune, PRD
  // #378): a wave's total XP (waveSize x xp) ramps smoothly across tier
  // boundaries (40 -> 77 -> 154 -> 289 -> 480 -> 768 -> 1148 -> 1728, no jump
  // past ~2x) so XP/second tracks the geometric `arenaXpToNext` curve and dings
  // land on a steady beat all run. Tier 1 is pinned (xp 5, waveSize 8 - the
  // first-draft reachability); the cadence-guard test asserts the band
  // symbolically from these very rows, so retune them together.
  //
  // Eight themed tiers drawn from the expanded bestiary (ADR-0211): critters ->
  // beasts -> restless dead -> warbands -> brutes -> dark casters -> elementals &
  // terathans -> the endgame. Each spawn picks seeded-uniform from its tier's
  // list; stats are uniform within a tier. Slime + snake are deliberately NOT
  // arena waves (hard-to-read sprites, ADR-0211) though they remain in the shared
  // {@link ../sim/content.monsters bestiary} for other minigames.
  { startTick: 0, monsters: ['mongbat', 'giant-spider', 'scorpion', 'imp'], health: 20, damageMin: 2, damageMax: 5, msPerTile: 320, waveSize: 8, xp: 5 },
  { startTick: 2000, monsters: ['dire-wolf', 'hell-hound', 'harpy', 'stone-harpy', 'dread-spider', 'corpser', 'reaper'], health: 45, damageMin: 4, damageMax: 8, msPerTile: 280, waveSize: 11, xp: 7 },
  { startTick: 4000, monsters: ['spirit', 'zombie', 'skeleton', 'skeleton-warrior', 'ghoul'], health: 90, damageMin: 6, damageMax: 11, msPerTile: 250, waveSize: 14, xp: 11 },
  { startTick: 6000, monsters: ['orc', 'orc-captain', 'orc-warrior', 'lizardman', 'ratman', 'ratman-archer', 'ettin'], health: 150, damageMin: 9, damageMax: 14, msPerTile: 220, waveSize: 17, xp: 17 },
  { startTick: 8000, monsters: ['ogre', 'ogre-lord', 'troll', 'gazer', 'headless-one', 'gargoyle', 'stone-gargoyle', 'cyclops'], health: 240, damageMin: 12, damageMax: 18, msPerTile: 200, waveSize: 20, xp: 24 },
  { startTick: 10000, monsters: ['lich', 'lich-lord', 'bone-magi', 'skeletal-knight', 'ratman-mage', 'ophidian-mage', 'ophidian-warrior', 'mummy', 'rotting-corpse'], health: 380, damageMin: 16, damageMax: 24, msPerTile: 180, waveSize: 24, xp: 32 },
  { startTick: 12000, monsters: ['air-elemental', 'earth-elemental', 'fire-elemental', 'water-elemental', 'blood-elemental', 'terathan-warrior', 'terathan-drone', 'terathan-avenger'], health: 560, damageMin: 20, damageMax: 30, msPerTile: 165, waveSize: 28, xp: 41 },
  { startTick: 14000, monsters: ['efreet', 'arctic-ogre-lord', 'titan', 'daemon', 'gate-daemon', 'ice-fiend', 'wyvern', 'drake', 'dragon', 'white-wyrm', 'shadow-wyrm', 'ancient-wyrm'], health: 820, damageMin: 26, damageMax: 38, msPerTile: 150, waveSize: 32, xp: 53 },
];

/**
 * Every wave creature across all tiers, deduped in weak->strong tier order
 * (ADR-0211). The endless continuation past the last defined tier draws from
 * this instead of only the final tier's four bodies, so a long run keeps mixing
 * in the whole bestiary at escalating stats rather than flatlining on dragons.
 */
export const ARENA_FULL_ROSTER: readonly string[] = [
  ...new Set(ARENA_STAGES.flatMap((s) => s.monsters)),
];

/** One interval-elite definition: which bestiary creature + its base (pre-escalation) stats. Same shape a wave tier carries per enemy. */
export interface ArenaEliteSpec {
  /** Bestiary monster id (its body comes from the {@link monsters} record). */
  monster: string;
  health: number;
  damageMin: number;
  damageMax: number;
  msPerTile: number;
  xp: number;
}

/**
 * The ordered elite roster (progressive elites): twelve distinct bestiary
 * bodies, weakest -> strongest, each stat block tuned to ramp monotonically.
 * The spawner walks this list two-of-each ({@link ARENA_ELITE_SEQUENCE_REPEATS})
 * for the first {@link ARENA_ELITE_FIXED_SPAWNS} elites, then rolls a random
 * type each time past that and escalates it geometrically (the "bigger" phase).
 * A pristine run therefore meets the whole elite bestiary in order before the
 * curve turns open-ended.
 */
export const ARENA_ELITE_SEQUENCE: readonly ArenaEliteSpec[] = [
  { monster: 'gibberling', health: 190, damageMin: 7, damageMax: 11, msPerTile: 155, xp: 30 },
  { monster: 'mound-of-maggots', health: 230, damageMin: 8, damageMax: 12, msPerTile: 175, xp: 34 },
  { monster: 'patchwork-skeleton', health: 280, damageMin: 9, damageMax: 13, msPerTile: 150, xp: 39 },
  { monster: 'bone-demon', health: 336, damageMin: 10, damageMax: 15, msPerTile: 150, xp: 45 },
  { monster: 'gore-fiend', health: 408, damageMin: 11, damageMax: 17, msPerTile: 145, xp: 52 },
  { monster: 'devourer', health: 492, damageMin: 13, damageMax: 19, msPerTile: 145, xp: 60 },
  { monster: 'flesh-golem', health: 596, damageMin: 15, damageMax: 22, msPerTile: 170, xp: 69 },
  { monster: 'impaler', health: 722, damageMin: 17, damageMax: 25, msPerTile: 140, xp: 80 },
  { monster: 'wanderer', health: 874, damageMin: 20, damageMax: 29, msPerTile: 140, xp: 92 },
  { monster: 'darknight-creeper', health: 1056, damageMin: 23, damageMax: 33, msPerTile: 150, xp: 106 },
  { monster: 'abysmal-horror', health: 1278, damageMin: 26, damageMax: 38, msPerTile: 150, xp: 122 },
  { monster: 'demon-knight', health: 1548, damageMin: 30, damageMax: 44, msPerTile: 145, xp: 150 },
] as const;

/** How many consecutive spawns of each ordered type before advancing (two of each, back-to-back at the normal interval). */
export const ARENA_ELITE_SEQUENCE_REPEATS = 2;

/**
 * How many elite spawns come from the fixed ordered sequence before the random
 * "bigger" phase begins: two of each of the twelve types (2 x 12).
 */
export const ARENA_ELITE_FIXED_SPAWNS =
  ARENA_ELITE_SEQUENCE.length * ARENA_ELITE_SEQUENCE_REPEATS;

/** Per-step health multiplier applied to a random-phase elite's base health (compounds with the step index). */
export const ARENA_ELITE_RANDOM_HEALTH_GROWTH = 1.14;
/** Per-step multiplier applied to a random-phase elite's base damage (both bounds). */
export const ARENA_ELITE_RANDOM_DAMAGE_GROWTH = 1.1;
/** Per-step multiplier applied to a random-phase elite's base XP. */
export const ARENA_ELITE_RANDOM_XP_GROWTH = 1.1;
/** Sprite scale of the *first* random-phase elite (already noticeably bigger than a normal elite). */
export const ARENA_ELITE_RANDOM_SCALE_BASE = 1.25;
/** Additional sprite scale gained per random-phase step. */
export const ARENA_ELITE_RANDOM_SCALE_STEP = 0.06;
/** Hard cap on random-phase elite sprite scale (keeps a late giant readable, not screen-filling). */
export const ARENA_ELITE_RANDOM_SCALE_CAP = 2.2;

/**
 * Backward-compat alias for the single old interval elite (the Abysmal Horror),
 * now entry #11 of the ordered sequence. Existing imports/re-exports keep
 * resolving; new code should read {@link ARENA_ELITE_SEQUENCE} directly.
 */
export const ARENA_ELITE = ARENA_ELITE_SEQUENCE[10]!;

/** One recurring-boss definition: which bestiary creature + its base (pre-escalation) stats. */
export interface ArenaBoss {
  /**
   * Bestiary monster id - the boss's **sprite** (body + sound) comes from this
   * {@link monsters} record. Two bosses may share one id (the Ultima roster has
   * more villains than dedicated boss bodies); the {@link ArenaBoss.name} below,
   * not the record's name, is what the runner sees.
   */
  monster: string;
  /**
   * The boss's dramatic **display name** - an Ultima villain (ADR-0211). Floated
   * overhead on spawn and shouted in the entrance banner/herald, deliberately
   * distinct from the sprite's bestiary name so villains can share a body.
   */
  name: string;
  /** The private journal line announcing this villain's entrance (its herald). */
  herald: string;
  health: number;
  damageMin: number;
  damageMax: number;
  msPerTile: number;
  xp: number;
}

/**
 * The recurring-boss roster (ADR-0211): the villains of Ultima. Each boss
 * milestone spawns `ARENA_BOSSES[bossKills % length]` (stat-escalated by
 * `bossKills`), so the climax spikes rotate through the great enemies - the
 * usurper Blackthorn, the Triad (Mondain, Minax, Exodus), and the Shadowlords
 * (Faulinei, Astaroth, Nosfentor) - each with its own name + herald. Their
 * sprite bodies are dedicated - **none appears in a wave tier** - so the client's
 * {@link ../sim/content.isArenaBossBody} set-check can only ever fire on a real
 * boss. Blackthorn stays first (a never-recurring run still meets him). Since
 * there are more villains than boss bodies, some share a sprite (Mondain/Astaroth
 * wear the Balron, Minax/Nosfentor the Semidar) - the name tells them apart.
 */
export const ARENA_BOSSES: readonly ArenaBoss[] = [
  { monster: 'demon-lord', name: 'Lord Blackthorn', health: 6000, damageMin: 20, damageMax: 30, msPerTile: 140, xp: 500,
    herald: 'Lord Blackthorn, the false regent, strides into your arena!' },
  { monster: 'balron', name: 'Mondain', health: 6400, damageMin: 22, damageMax: 32, msPerTile: 140, xp: 520,
    herald: 'Mondain the Wizard tears a rift into your arena!' },
  { monster: 'semidar', name: 'Minax', health: 6800, damageMin: 22, damageMax: 34, msPerTile: 140, xp: 540,
    herald: 'Minax the Enchantress sweeps into your arena!' },
  { monster: 'rikktor', name: 'Exodus', health: 7200, damageMin: 24, damageMax: 34, msPerTile: 145, xp: 560,
    herald: 'Exodus, neither man nor machine, grinds into your arena!' },
  { monster: 'mephitis', name: 'Faulinei', health: 6000, damageMin: 20, damageMax: 30, msPerTile: 135, xp: 500,
    herald: 'Faulinei, Shadowlord of Falsehood, seeps into your arena!' },
  { monster: 'balron', name: 'Astaroth', health: 6600, damageMin: 22, damageMax: 33, msPerTile: 140, xp: 530,
    herald: 'Astaroth, Shadowlord of Hatred, descends upon your arena!' },
  { monster: 'semidar', name: 'Nosfentor', health: 7000, damageMin: 23, damageMax: 34, msPerTile: 140, xp: 550,
    herald: 'Nosfentor, Shadowlord of Cowardice, coils into your arena!' },
];

/** The first boss (Lord Blackthorn): kept as an alias for the always-present opening climax. */
export const ARENA_BOSS = ARENA_BOSSES[0]!;

/** The first boss's display name (Lord Blackthorn), floated overhead on spawn. */
export const ARENA_BOSS_NAME = ARENA_BOSS.name;

/**
 * Every recurring-boss body id (ADR-0211) - the client keys the boss entrance
 * warning + death burst off membership here (`main.ts`), so a rotating boss body
 * still lights the climax chrome. Dedicated bodies (absent from the wave tiers).
 */
export const ARENA_BOSS_BODIES: ReadonlySet<number> = new Set(
  ARENA_BOSSES.map((b) => monsterById(b.monster)!.body),
);

/** Whether a body id is one of the recurring arena bosses (ADR-0211). */
export function isArenaBossBody(body: number | undefined): boolean {
  return body !== undefined && ARENA_BOSS_BODIES.has(body);
}

/**
 * The roster tier in force at `elapsedTicks`. Up to the last defined
 * {@link ARENA_STAGES} tier this is simply the last stage whose `startTick` has
 * passed. **Beyond** the last tier the arena escalates endlessly (ADR-0210):
 * every {@link ARENA_ENDLESS_STAGE_INTERVAL_TICKS} it derives a synthetic tier
 * from the final one, geometrically stronger (see the endless growth knobs),
 * drawing from the {@link ARENA_FULL_ROSTER whole bestiary} (ADR-0211) - so there
 * is always a harder wave than the last, its bodies keep varying, and a run is
 * bounded only by the runner. Pure and deterministic.
 */
export function arenaStageFor(elapsedTicks: number): ArenaStage {
  const last = ARENA_STAGES[ARENA_STAGES.length - 1]!;
  let current = ARENA_STAGES[0]!;
  for (const stage of ARENA_STAGES) {
    if (elapsedTicks >= stage.startTick) current = stage;
  }
  if (current !== last) return current;
  // Past the final defined tier: synthesize the endless continuation.
  const steps = Math.floor((elapsedTicks - last.startTick) / ARENA_ENDLESS_STAGE_INTERVAL_TICKS);
  if (steps <= 0) return last;
  return {
    startTick: last.startTick + steps * ARENA_ENDLESS_STAGE_INTERVAL_TICKS,
    // The whole bestiary (ADR-0211), not just the final tier - deep runs keep
    // seeing fresh bodies weakest-to-strongest, at the escalated stats below.
    monsters: ARENA_FULL_ROSTER,
    health: Math.round(last.health * ARENA_ENDLESS_HEALTH_GROWTH ** steps),
    damageMin: Math.round(last.damageMin * ARENA_ENDLESS_DAMAGE_GROWTH ** steps),
    damageMax: Math.round(last.damageMax * ARENA_ENDLESS_DAMAGE_GROWTH ** steps),
    msPerTile: Math.max(ARENA_ENDLESS_MIN_MS_PER_TILE, last.msPerTile - steps * ARENA_ENDLESS_GAIT_STEP),
    waveSize: last.waveSize + steps * ARENA_ENDLESS_WAVESIZE_STEP,
    xp: Math.round(last.xp * ARENA_ENDLESS_XP_GROWTH ** steps),
  };
}

/** A resolved interval-elite spawn: the {@link ArenaEliteSpec} stats plus an optional sprite scale (random-phase giants only). */
export interface ArenaEliteSpawn extends ArenaEliteSpec {
  /** Sprite scale (>1) for a random-phase elite; omitted (normal size) for the ordered sequence. */
  scale?: number;
}

/**
 * The elite to spawn for the `n`-th elite (0-based) of a run (progressive
 * elites). While `n <` {@link ARENA_ELITE_FIXED_SPAWNS} this walks
 * {@link ARENA_ELITE_SEQUENCE} {@link ARENA_ELITE_SEQUENCE_REPEATS}-of-each in
 * order at normal size. Past that it enters the random "bigger" phase: a random
 * type off the run's **seeded** rng (`world.rng`, so replays stay identical),
 * escalated geometrically by the step index `t = n - ARENA_ELITE_FIXED_SPAWNS`
 * and rendered at a growing (capped) sprite scale. Exactly one rng draw is made
 * per random-phase spawn (the type roll), keeping the seeded stream aligned.
 */
export function arenaEliteSpawnFor(world: World, n: number): ArenaEliteSpawn {
  if (n < ARENA_ELITE_FIXED_SPAWNS) {
    return ARENA_ELITE_SEQUENCE[Math.floor(n / ARENA_ELITE_SEQUENCE_REPEATS)]!;
  }
  const t = n - ARENA_ELITE_FIXED_SPAWNS;
  const base = ARENA_ELITE_SEQUENCE[world.rng.nextInt(ARENA_ELITE_SEQUENCE.length)]!;
  return {
    monster: base.monster,
    health: Math.round(base.health * ARENA_ELITE_RANDOM_HEALTH_GROWTH ** t),
    damageMin: Math.round(base.damageMin * ARENA_ELITE_RANDOM_DAMAGE_GROWTH ** t),
    damageMax: Math.round(base.damageMax * ARENA_ELITE_RANDOM_DAMAGE_GROWTH ** t),
    msPerTile: base.msPerTile,
    xp: Math.round(base.xp * ARENA_ELITE_RANDOM_XP_GROWTH ** t),
    scale: Math.min(
      ARENA_ELITE_RANDOM_SCALE_CAP,
      ARENA_ELITE_RANDOM_SCALE_BASE + ARENA_ELITE_RANDOM_SCALE_STEP * t,
    ),
  };
}

/**
 * Resolve a bestiary monster id to the UO body a horde spawn renders as. An
 * unknown id is a config error - throw loudly at spawn (the fatal-bake
 * discipline; every id in the stage tables is registry-checked by tests).
 */
function monsterAppearance(monster: string): { body: number; bodyHue?: number } {
  const def = monsterById(monster);
  if (!def) throw new Error(`arena: unknown bestiary monster '${monster}'`);
  return { body: def.body, bodyHue: def.hue };
}

// ---------------------------------------------------------------------------
// The run state.
// ---------------------------------------------------------------------------

/**
 * How a run is (or stopped) advancing. `paused-draft` is Slice C's level-up
 * pause: {@link stepArena} is a complete no-op while it holds, freezing the
 * clock, the spawn schedule, and the horde in one flag.
 */
export type ArenaRunStatus = 'running' | 'paused-draft' | 'ended';

/** How an ended run resolved: survived to the horn, or died to the horde. */
export type ArenaRunOutcome = 'survived' | 'died';

/**
 * The runner's in-run build (fire-mage rework, ADR-0179): the one base skill's
 * volley pacing plus the drafted modifier and passive-stat levels. The skill
 * itself is fixed ({@link ./arenaWeapons.ARENA_SKILL_ID Magic Arrow}); the
 * build IS the modifiers. Grown only by {@link applyArenaPick} (the level-up
 * draft).
 */
export interface ArenaBuild {
  /** Ticks until the next volley; `0` = ready (fires on the first tick with a target in range). */
  skillCooldown: number;
  /** Drafted fire-modifier levels keyed by id (absent = level 0 = not drafted). */
  modifiers: ArenaModifierLevels;
  /** Passive stat levels keyed by stat id. */
  stats: Partial<Record<ArenaStatId, number>>;
  /** Owned keystone transcendences, keyed by the modifier each transforms (PRD #378). */
  keystones: Set<ArenaModifierId>;
  /** Volleys fired since the last nova ring (PRD #378) - at
   * {@link ./arenaWeapons.novaInterval} the next volley also rings. */
  volleysSinceNova: number;
  /** Repeatable ascension counts (PRD #378) - the endless late-run picks. */
  ascensions: ArenaAscensions;
}

/**
 * One option of a level-up draft (ADR-0179): a fire-modifier raise or a
 * passive-stat raise. `toLevel` is the level the pick lands on, so the client
 * can render "Chain -> Lv 3" without re-deriving it - and so a stale/forged
 * pick is detectable ({@link applyArenaPick} rejects a `toLevel` that is not
 * exactly current+1).
 */
export type ArenaDraftOption =
  | { kind: 'modifier'; modifier: ArenaModifierId; toLevel: number }
  | { kind: 'stat'; stat: ArenaStatId; toLevel: number }
  /** A keystone transcendence (PRD #378): binary, offered once the named
   * modifier is maxed and un-transcended ({@link ./arenaWeapons.ARENA_KEYSTONES}). */
  | { kind: 'keystone'; modifier: ArenaModifierId }
  /** The rare Flame Vortex summon (PRD #378): repeatable, immediate - picking
   * it raises a fire-hued vortex that hunts the horde for a while. */
  | { kind: 'vortex' }
  /** An endless Ascension (PRD #378): offered once everything finite is owned,
   * repeating forever. `count` is the pick number it lands on (the `toLevel`
   * staleness defense, unbounded). */
  | { kind: 'ascension'; ascension: ArenaAscensionId; count: number };

/**
 * A pending level-up draft offer: rolled seeded from the run world's rng when
 * an XP threshold is crossed ({@link rollArenaDraft}), held while the run is
 * `paused-draft`, consumed by {@link applyArenaPick}.
 */
export interface ArenaDraftOffer {
  /** The level this draft rewards (the one just reached), for the client overlay. */
  level: number;
  /** The seeded option list (up to {@link ARENA_DRAFT_SIZE} distinct entries). */
  options: ArenaDraftOption[];
}

/**
 * One burning ground zone (PRD #378: Scorched Earth trail / Cataclysm ground) -
 * **run state, not an entity**: the run world is private (ADR-0176), so a zone
 * never needs a snapshot entity; it ships once as an owner-only `fire-field`
 * FX event with a duration and the client loops the field strip client-side.
 * Ticked inside {@link stepArena} against live enemies through the shared
 * combat seam on the {@link ./arenaWeapons.ARENA_ZONE_BURN_INTERVAL_TICKS}
 * cadence. Burn damage floats NO numbers by design - the field visual is the
 * telegraph, and a kited horde in a long trail would flood the float budget.
 */
export interface ArenaZone {
  /** The burning tiles, frozen at creation (the damage footprint = the FX footprint). */
  tiles: ArenaFxTile[];
  /** Absolute run tick the field gutters out (pruned at the next burn tick). */
  expiresAtTick: number;
  /** Damage dealt to each live enemy standing in the field, per burn interval. */
  damage: number;
}

/**
 * Cap on concurrently-burning zones: at the cap the OLDEST zone gutters early
 * (never the new one - a kiting Scorched Earth runner keeps painting their
 * trail). Bounds both the burn tick's work and the FX the client loops.
 */
export const ARENA_MAX_ZONES = 48;

/** Milliseconds per run tick (the 50ms fixed step) - for tick-span -> FX-duration math. */
export const ARENA_TICK_MS = 50;

// --- Flame Vortex (PRD #378): a rare draft that summons a hunting ally. -----

/** How long a summoned Flame Vortex hunts before dissipating - 30 seconds. */
export const ARENA_FLAME_VORTEX_LIFETIME_TICKS = 600;

/** The chance a level-up draft's pool includes the Flame Vortex offer (rolled
 * seeded, per draft) - rare, repeatable, and never crowding the core picks. */
export const ARENA_VORTEX_OFFER_CHANCE = 0.2;

/** The Flame Vortex's gait: the storm vortex's 140ms/tile - faster than every
 * horde tier, so it genuinely hunts rather than trails. */
export const ARENA_FLAME_VORTEX_MS_PER_TILE = 140;

/** Inclusive bite-damage range per landed hit (once a second at adjacency). */
export const ARENA_FLAME_VORTEX_DAMAGE = { min: 15, max: 25 } as const;

/** The fire hue painted over the energy-vortex body (a UO hue-table orange).
 * VERIFY by eye at the captain's bake; the PRD's fallback is a runtime tint. */
export const ARENA_FLAME_VORTEX_HUE = 43;

/**
 * The whole run's choreography state - ephemeral server memory beside the run's
 * private world (never persisted, never snapshotted; the {@link ./storm.StormState}
 * / RatSwarmState model). Mutated only by {@link stepArena} (and, for the Slice C
 * fields, by the draft handlers when that slice lands).
 */
export interface ArenaRunState {
  status: ArenaRunStatus;
  /** Versioned rules captured at entry and never re-read from the registry. */
  challenge: ArenaChallengeDefinition;
  /**
   * The runner's chosen class def (PRD #446, Slice 0), captured **once at
   * start** and never mutated mid-run (the `enemyCap`/`StormTuning` capture
   * pattern). The firing gate reads `firesWhileMoving` and the draft pool reads
   * the modifier/keystone lines through it; an absent `classId` at start
   * resolves to the fire mage, so a run is always a concrete class.
   */
  classDef: ArenaClassDef;
  /** How the run ended; `null` while it is still live. */
  outcome: ArenaRunOutcome | null;
  /** The runner's entity id in the run world (spawned by {@link startArenaRun}). */
  player: EntityId;
  /** Ticks elapsed while running (the pause does not count); the run's whole clock. */
  elapsedTicks: number;
  /** Ids of every horde enemy currently alive (reaped as they die). */
  enemies: Set<EntityId>;
  /** Ids of the live **interval elites** (a subset of {@link enemies}), tracked
   * so a reap can tell an elite from a regular for the Vacuum drop roll (PRD
   * #431 Slice B). Pruned as they die. */
  elites: Set<EntityId>;
  /** The horde pursuit brains (one per enemy), stepped from inside {@link stepArena}. */
  pursuit: HordePursuitState;
  /** Waves spawned so far (the opening wave is wave 1). */
  wave: number;
  /** Absolute tick the next regular wave fires. */
  nextWaveTick: number;
  /** Absolute tick the next elite spawns (deferred a tick at a time while the field is full). */
  nextEliteTick: number;
  /**
   * How many interval elites have spawned this run (0-based index of the next
   * one). Drives the progressive-elite selection: the first
   * {@link ARENA_ELITE_FIXED_SPAWNS} walk {@link ARENA_ELITE_SEQUENCE} two of
   * each in order; past that each spawn rolls a random type and escalates it.
   */
  eliteSpawnCount: number;
  /** Whether the first boss milestone has fired (gates the pre-boss spawn reserve). */
  bossSpawned: boolean;
  /** Absolute tick the next boss milestone is due (ADR-0210: recurring, one at a time). */
  nextBossTick: number;
  /** Bosses fully defeated this run - each worth {@link ARENA_SCORE_BOSS_BONUS} (cumulative). */
  bossKills: number;
  /** The boss's entity id while it lives (`null` before spawn and after its death). */
  boss: EntityId | null;
  /** Fraction of the **current** boss's health removed so far, 0..1 (reset to 0 when it dies
   * and its bar is banked into {@link ArenaRunState.bossKills}). */
  bossProgress: number;
  /** Whether the boss currently runs its beyond-the-radius catch-up gait
   * (PRD #387 Slice C) - the edge detector for the gait rebuild. */
  bossCatchup: boolean;
  /**
   * This run's live-enemy cap, captured **once at start** from the concurrency
   * governor ({@link arenaEnemyBudget}, PRD #387 Slice D) - never mutated
   * mid-run. Waves clamp to it and elites defer at it exactly as they always
   * did against the old module constant.
   */
  enemyCap: number;
  /** Enemies killed this run (any death of a live enemy counts - Slice C's score input). */
  kills: number;
  // --- Slice C: level/XP/build/draft (ADR-0175). ---
  /** The runner's in-run level; starts at 1, advanced by the XP curve. */
  level: number;
  /** XP toward the next level; each reaped kill feeds it. */
  xp: number;
  /** The runner's build (weapons + passives); starts with the starting weapon. */
  build: ArenaBuild;
  /** The held draft offer while `paused-draft`; `null` while running/ended. */
  pendingDraft: ArenaDraftOffer | null;
  /** XP each live enemy is worth on death, recorded at spawn (its roster tier's value). */
  enemyXp: Map<EntityId, number>;
  /**
   * Ground XP gems, oldest first (VS-style orbs, `./xpGem`): a kill drops its
   * tier XP as a gem at the corpse tile, and the runner collects it by walking
   * within the magnet radius - XP lands at pickup, not at death.
   */
  gems: EntityId[];
  /**
   * Live **Vacuum** pickups on the field (PRD #431 Slice B, `./xpGem`): a rare
   * elite/boss drop, capped at {@link ARENA_VACUUM_FIELD_CAP}. Claiming one
   * ({@link stepArena} step 3.4) collects the whole {@link gems} field at once.
   */
  vacuums: EntityId[];
  // --- Stop-to-cast (ADR-0191). ---
  /** The runner's tile last tick, for the stillness watch (see {@link ARENA_CAST_SETTLE_TICKS}). */
  lastPlayerTile: { x: number; y: number };
  /** Consecutive ticks the runner has held the same tile; the skill fires only at
   * >= {@link ARENA_CAST_SETTLE_TICKS}. Starts settled (the run opens armed AND still). */
  stillTicks: number;
  /** Last tick through which an accepted predicted step is still in flight visually. */
  movingUntilTick: number;
  // --- Draft expansion (PRD #378). ---
  /** Burning ground zones (Scorched Earth trail / Cataclysm ground), oldest first. */
  zones: ArenaZone[];
  /**
   * Each live enemy's per-enemy tracking record (PRD #419): the tile as of
   * last tick (the entry-burn record - {@link stepArenaZones} checks it
   * **every tick** and an enemy whose tile changed onto a burning tile pays
   * one burn immediately, so every trail crossing costs regardless of dwell
   * time) plus the Combustion keystone's `burningUntilTick` window (Slice C -
   * stamped by every zone burn, consumed by a detonation). Maintained every
   * tick even with no zones alight (a stale record would fake crossings the
   * moment one ignites); pruned as enemies die. The record is **mutated in
   * place** on a tile change, never replaced, so the burning window survives
   * the move.
   */
  enemyLastTiles: Map<EntityId, ArenaEnemyRecord>;
  /** Meteors in the air (Meteor keystone): telegraphed at proc, landing at
   * `impactAtTick` - the storm's `pendingSummon` shape. */
  pendingMeteors: ArenaMeteor[];
  /** Live summoned Flame Vortexes and when each dissipates (PRD #378). */
  vortexes: { id: EntityId; expiresAtTick: number }[];
  /** The summons' pursuit brains ({@link ./vortex.stepVortexPursuit}), targets
   * flipped onto the horde - stepped from inside {@link stepArena}. */
  vortexPursuit: VortexPursuitState;
  // --- Archer entity DoTs / ground slows (PRD #446 Slice 1, ADR-0237). Run
  // state, never run-world entity components (ADR-0176); ticked in
  // stepArcherStatuses (step 4) and pruned against a per-tick `seen` set exactly
  // like enemyLastTiles. Empty for every non-archer class. ---
  /** Live bleed wounds (Barbed Arrows), keyed by enemy id. The shared bleed table. */
  bleeds: Map<EntityId, ArenaBleed>;
  /** Live caltrop slows (Caltrops), keyed by enemy id - gait restored on expiry. */
  slows: Map<EntityId, ArenaSlow>;
  /** Dropped caltrop tiles behind the runner (Caltrops), oldest first - the slow
   * source; a separate list from {@link zones} because caltrops don't damage. */
  caltrops: ArenaCaltrop[];
}

/**
 * One enemy's per-tick tracking record ({@link ArenaRunState.enemyLastTiles}):
 * its tile as of last tick (the PRD #419 entry-burn watch) plus, once any zone
 * burn has touched it, the tick its **burning** state lapses (the Combustion
 * keystone's detonation window, PRD #419 Slice C). Burning is zone-generic by
 * design - trail, Cataclysm ground, and meteor pool burns all stamp it, entry
 * or interval alike - only the *detonation* is gated on the keystone.
 */
export interface ArenaEnemyRecord extends ArenaFxTile {
  /** Absolute run tick the enemy stops counting as burning; absent = never
   * burned (or its one detonation already consumed the window). */
  burningUntilTick?: number;
}

/** One telegraphed meteor in flight (Meteor keystone, PRD #378). */
export interface ArenaMeteor {
  /** The impact tile, aimed at the densest cluster at proc time. */
  at: ArenaFxTile;
  /** Absolute run tick the star lands. */
  impactAtTick: number;
  /** Impact damage, frozen from the proccing hit ({@link ./arenaWeapons.meteorDamage}). */
  damage: number;
}

/**
 * One enemy's live **bleed** wound (archer Barbed Arrows, PRD #446 Slice 1,
 * ADR-0237) - the first Arena *entity DoT*, held on {@link ArenaRunState.bleeds}
 * keyed by enemy id (never on the run-world entity, ADR-0176). Damage lands
 * through the shared combat seam on the {@link ./arenaClasses/archer.ARCHER_BLEED_INTERVAL_TICKS}
 * cadence, ticked in {@link stepArcherStatuses}. Re-applying a barb deepens the
 * wound (another stack, to the cap) and refreshes `expiresAtTick`.
 */
export interface ArenaBleed {
  /** Absolute run tick the wound clots (pruned at the next status tick). */
  expiresAtTick: number;
  /** Damage ONE stack ticks for, frozen from the barb's live arrow damage. */
  damagePerStack: number;
  /** Live stacks, 1..{@link ./arenaClasses/archer.ARCHER_BLEED_MAX_STACKS}. */
  stacks: number;
}

/**
 * One enemy's live **caltrop slow** (archer Caltrops, PRD #446 Slice 1) - keyed
 * by enemy id on {@link ArenaRunState.slows}. Applying it sets the enemy mobile's
 * {@link ./mobile.Mobile.setSlowFactor} (safe: the run world is private and torn
 * down at run end); on expiry the factor is cleared back to 1.
 */
export interface ArenaSlow {
  /** Absolute run tick the slow lifts (refreshed on each fresh caltrop contact). */
  expiresAtTick: number;
}

/** One dropped **caltrop** tile (archer Caltrops) - a slow marker on the ground
 * behind the runner, NOT a damage zone (so it never joins {@link ArenaRunState.zones}). */
export interface ArenaCaltrop extends ArenaFxTile {
  /** Absolute run tick the caltrop is spent (pruned at the next status tick). */
  expiresAtTick: number;
}

/** A seeded facing for a freshly-placed actor (the `spawnRats` facing draw). */
function seededFacing(world: World): Direction {
  return ALL_DIRECTIONS[world.rng.nextInt(ALL_DIRECTIONS.length)]!;
}

/**
 * A seeded tile on the spawn ring around the runner (PRD #387 Slice A): pick
 * an angle - from the {@link ARENA_SPAWN_FRONT_ARC front arc} around `heading`
 * with {@link ARENA_SPAWN_FRONT_WEIGHT}, else uniform on the full circle - and
 * project it to Chebyshev distance {@link ARENA_SPAWN_RADIUS} (the max
 * component lands exactly on the ring). `frontal: true` (recycling) always
 * draws from the front arc, so an outrun enemy reappears **ahead** of the
 * runner. The heading is the runner's facing - the sim's proxy for movement
 * heading (facing mirrors the movement direction while walking).
 *
 * The battlefield is an unbounded open plain (PRD #387 Slice C), so the raw
 * ring tile is always walkable and needs no clamping - Slice A's wall clamp
 * went with the wall.
 *
 * Draw order is fixed (one gate roll, then one angle roll) so a seeded run
 * replays byte-identically. Exported for the sim tests.
 */
export function seededRingSpot(
  world: World,
  runner: SpawnPoint,
  heading: Direction,
  opts: { frontal?: boolean } = {},
): SpawnPoint {
  const { dx: hx, dy: hy } = deltaForDirection(heading);
  const headingAngle = Math.atan2(hy, hx);
  const front = opts.frontal || world.rng.next() < ARENA_SPAWN_FRONT_WEIGHT;
  const angle = front
    ? headingAngle + (world.rng.next() * 2 - 1) * ARENA_SPAWN_FRONT_ARC
    : world.rng.next() * 2 * Math.PI;
  const ux = Math.cos(angle);
  const uy = Math.sin(angle);
  const scale = ARENA_SPAWN_RADIUS / Math.max(Math.abs(ux), Math.abs(uy));
  return { x: runner.x + Math.round(ux * scale), y: runner.y + Math.round(uy * scale) };
}

/**
 * The runner-anchored frame every entrance/recycle placement derives from:
 * the runner's tile + facing. A downed/vanished runner (the run is ending
 * this very tick) falls back to the last tracked tile so a same-tick spawn
 * can never throw.
 */
function runnerAnchor(world: World, state: ArenaRunState): { spot: SpawnPoint; heading: Direction } {
  const player = world.get(state.player);
  if (player?.position) {
    return {
      spot: { x: player.position.x, y: player.position.y },
      heading: player.position.facing,
    };
  }
  return { spot: { ...state.lastPlayerTile }, heading: Direction.S };
}

/** Spawn one regular-wave enemy of the given tier on a seeded ring tile
 * around the runner, drawn seeded-uniform from the tier's bestiary list
 * (visual variety; the draw rides `world.rng` so replays stay byte-identical). */
function spawnWaveEnemy(world: World, state: ArenaRunState, stage: ArenaStage): EntityId {
  const anchor = runnerAnchor(world, state);
  const spot = seededRingSpot(world, anchor.spot, anchor.heading);
  const monster = stage.monsters[world.rng.nextInt(stage.monsters.length)]!;
  const enemy = spawnHordeEnemy(world, state.pursuit, {
    x: spot.x,
    y: spot.y,
    facing: seededFacing(world),
    ...monsterAppearance(monster),
    msPerTile: stage.msPerTile,
    health: stage.health,
    damageMin: stage.damageMin,
    damageMax: stage.damageMax,
  });
  state.enemies.add(enemy.id);
  state.enemyXp.set(enemy.id, stage.xp);
  return enemy.id;
}

/**
 * The identity the runner enters the arena with - their town look, passed
 * through by the server. All optional; the movement/spawn/gait decisions are
 * the arena's own ({@link ARENA_PLAYER_MS_PER_TILE}, the map's centre spawn).
 */
export type ArenaPlayerLook = Pick<
  PlayerOptions,
  'body' | 'bodyHue' | 'name' | 'title' | 'equipment' | 'maxHealth'
>;

/**
 * Start a run: spawn the runner at the arena's centre at run gait, spawn the
 * opening wave on the ring around them, and return the fresh run state. The
 * caller (the Arena server slice) has already built the run's private seeded
 * world around {@link ./arenaMap.makeArenaMap}'s data and passes the same
 * {@link ArenaMap} here so the choreography inherits its spawn anchor.
 */
export function startArenaRun(
  world: World,
  arena: Pick<ArenaMap, 'playerSpawn'>,
  look: ArenaPlayerLook = {},
  opts: { enemyCap?: number; classId?: ArenaClassId; challenge?: ArenaChallengeDefinition;
    initialBuild?: Partial<Pick<ArenaBuild, 'modifiers' | 'stats'>> } = {},
): ArenaRunState {
  // Resolve the chosen class (PRD #446): absent = the fire mage. An unknown id
  // is the server's refuse-in-fiction concern before it reaches here; this
  // path always receives a valid id (or none), so it defaults rather than
  // throws - a run is always a concrete class.
  const classDef = getArenaClass(opts.classId ?? DEFAULT_ARENA_CLASS_ID)!;
  const player = createPlayer(world, {
    x: arena.playerSpawn.x,
    y: arena.playerSpawn.y,
    facing: Direction.S,
    msPerTile: ARENA_PLAYER_MS_PER_TILE,
    ...look,
  });
  // The class's innate HP budget (PRD #446): the swordsman's +50% tank budget
  // lands here (Slice 2). Absent/1 = the shared baseline, so the fire mage is
  // byte-identical (the block is a no-op for a class with no `maxHpFactor`).
  const maxHpFactor = classDef.innate?.maxHpFactor;
  if (maxHpFactor !== undefined && maxHpFactor !== 1 && player.entity.health) {
    player.entity.health.max = Math.round(player.entity.health.max * maxHpFactor);
    player.entity.health.current = player.entity.health.max;
  }
  // The archer holds a bow (PRD #446 Slice 1, ADR-0237): a render-only `weapon`
  // layer on the runner (the halberd precedent), swapping to a crossbow once
  // Heavy Draw is drafted. Replicates through the ordinary renderable snapshot;
  // never leaks to town (the run entity is discarded on return).
  if (classDef.id === 'archer') equipArcherWeapon(player.entity, {});
  // No passive regen in the arena (ADR-0192): town regen (full heal in 45s)
  // out-healed the whole early game, making the opening unloseable. The duel's
  // regenPaused flag pauses it for the entire run - horde enemies already
  // carry it (horde.ts) - so the only heals are the `regen` draft's metered tick.
  // The flag never leaks to town: the return path spawns a fresh entity
  // (gameServer.respawnAtPortal) with fresh vitals. The `regen` draft layers
  // its own metered heal back on top (stepArena), gated behind a draft pick.
  player.entity.health!.regenPaused = true;

  const state: ArenaRunState = {
    status: 'running',
    challenge: captureArenaChallenge(opts.challenge ??
      ARENA_CHALLENGES.find((entry) => entry.id === DEFAULT_ARENA_CHALLENGE_ID)!),
    classDef,
    outcome: null,
    player: player.entity.id,
    elapsedTicks: 0,
    enemies: new Set(),
    elites: new Set(),
    pursuit: createHordePursuit(),
    wave: 0,
    nextWaveTick: ARENA_WAVE_INTERVAL_TICKS,
    nextEliteTick: ARENA_FIRST_ELITE_TICKS,
    eliteSpawnCount: 0,
    bossSpawned: false,
    nextBossTick: ARENA_BOSS_TICKS,
    bossKills: 0,
    boss: null,
    bossProgress: 0,
    bossCatchup: false,
    enemyCap: opts.enemyCap ?? ARENA_ENEMY_CAP,
    kills: 0,
    level: 1,
    xp: 0,
    // The run opens armed: the bare Magic Arrow, ready to fire; the modifiers
    // are what the drafts grow.
    build: {
      skillCooldown: 0,
      modifiers: { ...(opts.initialBuild?.modifiers ?? {}) },
      stats: { ...(opts.initialBuild?.stats ?? {}) },
      keystones: new Set(),
      volleysSinceNova: 0,
      ascensions: {},
    },
    pendingDraft: null,
    enemyXp: new Map(),
    gems: [],
    vacuums: [],
    // Stop-to-cast opens settled: the runner spawns planted at the centre, so
    // the opening volley fires the moment the first enemy closes into reach.
    lastPlayerTile: { x: arena.playerSpawn.x, y: arena.playerSpawn.y },
    stillTicks: ARENA_CAST_SETTLE_TICKS,
    movingUntilTick: -1,
    zones: [],
    enemyLastTiles: new Map(),
    pendingMeteors: [],
    vortexes: [],
    vortexPursuit: createVortexPursuit(),
    bleeds: new Map(),
    slows: new Map(),
    caltrops: [],
  };

  if (!isArenaBuildAllowed(state.build, state.challenge.rules)) {
    world.remove(player.entity.id);
    throw new Error(`arena: initial build violates challenge '${state.challenge.id}'`);
  }

  // The opening wave is on the field before the first tick - a horde game opens
  // under pressure, not with an empty arena.
  const stage = arenaStageFor(0);
  for (let i = 0; i < stage.waveSize; i++) spawnWaveEnemy(world, state, stage);
  state.wave = 1;

  return state;
}

/**
 * Populate a live run with the deterministic late-run density used by the
 * browser performance benchmark.
 * This is presentation-load setup only and is never called by ordinary runs.
 */
function seedArenaPerformanceScenarioAt(
  world: World,
  state: ArenaRunState,
  enemyCount: number,
  elapsedTicks: number,
): void {
  state.elapsedTicks = elapsedTicks;
  state.enemyCap = Math.max(state.enemyCap, enemyCount);
  const stage = arenaStageFor(state.elapsedTicks);
  while (state.enemies.size < enemyCount) spawnWaveEnemy(world, state, stage);

  for (const modifier of ARENA_MODIFIER_IDS) {
    state.build.modifiers[modifier] = ARENA_MODIFIER_MAX_LEVEL;
    state.build.keystones.add(modifier);
  }
  for (const stat of ARENA_STAT_IDS) state.build.stats[stat] = ARENA_STAT_MAX_LEVEL;
  state.build.ascensions = { power: 8, alacrity: 8 };
  state.build.skillCooldown = 0;
  state.level = 100;

  const player = world.get(state.player);
  if (player?.health) {
    player.health.max = 1_000_000;
    player.health.current = player.health.max;
  }

  const center = player?.position ?? { x: 40, y: 40 };
  for (let index = 0; index < ARENA_XP_GEM_CAP; index += 1) {
    const angle = (index / ARENA_XP_GEM_CAP) * Math.PI * 2;
    const radius = 12 + (index % 5);
    spawnXpGem(
      world,
      state.gems,
      {
        x: Math.round(center.x + Math.cos(angle) * radius),
        y: Math.round(center.y + Math.sin(angle) * radius),
      },
      index + 1,
    );
  }
}

export function seedArenaPerformanceScenario(
  world: World,
  state: ArenaRunState,
  enemyCount = ARENA_ENEMY_CAP,
): void {
  seedArenaPerformanceScenarioAt(world, state, enemyCount, 12 * 60 * 20);
}

/** Keep the browser benchmark at its declared density for the full soak. */
export function maintainArenaPerformanceScenario(
  world: World,
  state: ArenaRunState,
  enemyCount = ARENA_ENEMY_CAP,
): void {
  state.status = 'running';
  state.pendingDraft = null;
  state.xp = 0;
  state.enemyCap = Math.max(state.enemyCap, enemyCount);
  state.nextWaveTick = state.elapsedTicks + ARENA_WAVE_INTERVAL_TICKS;
  state.nextEliteTick = state.elapsedTicks + ARENA_ELITE_INTERVAL_TICKS;
  state.nextBossTick = state.elapsedTicks + ARENA_BOSS_INTERVAL_TICKS;
  const stage = arenaStageFor(state.elapsedTicks);
  while (state.enemies.size < enemyCount) spawnWaveEnemy(world, state, stage);

  const player = world.get(state.player);
  if (player?.health) player.health.current = player.health.max;
  const center = player?.position ?? { x: 40, y: 40 };
  while (state.gems.length < ARENA_XP_GEM_CAP) {
    const index = state.gems.length;
    const angle = (index / ARENA_XP_GEM_CAP) * Math.PI * 2;
    const radius = 12 + (index % 5);
    spawnXpGem(
      world,
      state.gems,
      {
        x: Math.round(center.x + Math.cos(angle) * radius),
        y: Math.round(center.y + Math.sin(angle) * radius),
      },
      index + 1,
    );
  }
}

const ARENA_PERFORMANCE_PROGRESSION_TICKS_PER_STEP = 25;

function clearArenaPerformanceEnemies(world: World, state: ArenaRunState): void {
  for (const id of [...state.enemies]) {
    if (id === state.boss) continue;
    world.remove(id);
    state.enemies.delete(id);
    state.elites.delete(id);
    state.pursuit.brains.delete(id);
    state.enemyXp.delete(id);
    state.enemyLastTiles.delete(id);
    state.bleeds.delete(id);
    state.slows.delete(id);
  }
}

/**
 * Start the real-browser progression benchmark at full opening-tier density.
 *
 * Unlike the fixed late-run benchmark, this mode deliberately makes the client
 * adopt and retire every authored monster tier before it reaches dragons.
 */
export function seedArenaProgressionPerformanceScenario(
  world: World,
  state: ArenaRunState,
  enemyCount = ARENA_ENEMY_CAP,
): void {
  seedArenaPerformanceScenarioAt(world, state, enemyCount, 0);
}

/**
 * Accelerate the benchmark through every authored tier and the first three
 * boss milestones, then continue at full density for a stable measurement.
 */
export function maintainArenaProgressionPerformanceScenario(
  world: World,
  state: ArenaRunState,
  enemyCount = ARENA_ENEMY_CAP,
): void {
  const finalStageTick = Math.max(
    ARENA_STAGES.at(-1)!.startTick + ARENA_ENDLESS_STAGE_INTERVAL_TICKS,
    ARENA_BOSS_TICKS + ARENA_BOSS_INTERVAL_TICKS * 2,
  );
  const progressionComplete = state.bossKills >= Math.min(3, ARENA_BOSSES.length);
  const benchmarkElapsedTicks = progressionComplete
    ? Math.min(state.elapsedTicks, finalStageTick)
    : state.elapsedTicks;
  const previousStage = arenaStageFor(benchmarkElapsedTicks);
  const nextElapsedTicks = progressionComplete
    ? finalStageTick
    : benchmarkElapsedTicks < finalStageTick
      ? Math.min(
          finalStageTick,
          benchmarkElapsedTicks + ARENA_PERFORMANCE_PROGRESSION_TICKS_PER_STEP,
        )
      : benchmarkElapsedTicks + ARENA_PERFORMANCE_PROGRESSION_TICKS_PER_STEP;
  const nextStage = arenaStageFor(nextElapsedTicks);
  if (nextStage.startTick !== previousStage.startTick) {
    clearArenaPerformanceEnemies(world, state);
  }
  state.elapsedTicks = nextElapsedTicks;
  const nextBossTick = state.nextBossTick;
  maintainArenaPerformanceScenario(world, state, enemyCount);
  state.nextBossTick = nextBossTick;
}

/**
 * Pause the run for a level-up draft (Slice C's hook): while `paused-draft`,
 * {@link stepArena} advances **nothing** - clock, spawns, pursuit, and bites are
 * all frozen, because the whole step early-returns on the status. The server
 * must also skip the paused run world's `world.tick` (see the module header) so
 * in-flight movement and regen freeze with it. Only a `running` run can pause
 * (an ended run stays ended).
 */
export function pauseArenaForDraft(state: ArenaRunState): void {
  if (state.status === 'running') state.status = 'paused-draft';
}

/** Resume a draft-paused run (Slice C's hook after the pick is applied). */
export function resumeArena(state: ArenaRunState): void {
  if (state.status === 'paused-draft') state.status = 'running';
}

// ---------------------------------------------------------------------------
// The choreography stepper.
// ---------------------------------------------------------------------------

/** A tile position an FX event anchors to, recorded at resolution time - the
 * enemy may be dead (or despawned) before the client animates, so the event
 * never depends on the entity still existing. */
export interface ArenaFxTile {
  x: number;
  y: number;
}

/**
 * One renderable step of a volley's cascade (fire-mage rework, ADR-0179), in
 * depth-first resolution order. `gen` is the cascade generation - 0 for a
 * primary projectile flight, 1 for its first on-hit consequence / first chain
 * hop, and so on - the client staggers the animation by it (flights, then
 * impacts, then children). Damage numbers ride here per struck entity,
 * replacing the old per-hit CombatText for skill hits; the sim has **already**
 * applied every point of damage through the combat seam by the time the event
 * is emitted.
 */
export type ArenaFxEvent =
  /** A primary projectile flight from the runner to a target. */
  | {
      kind: 'projectile';
      projectile: ArenaProjectileKind;
      /** Art scale tier (the Greater Projectile level; 0 = the bare arrow). */
      sizeTier: number;
      from: ArenaFxTile;
      to: ArenaFxTile;
      target: EntityId;
      damage: number;
      gen: number;
      /** Pierce machinery (PRD #378, ex Fire Serpent - dormant on the base
       * skill since Comet replaced that keystone, PRD #419 slice E): every
       * extra enemy the flight ran through (each ate the full `damage`); `to`
       * is the ray's END tile, so the serpent visibly flies through the
       * horde. Absent = a plain shot. */
      pierced?: EntityId[];
      /** Comet keystone (PRD #419 slice E): the sim-resolved tile the struck
       * enemy was hurled to along the flight line (walkability-clamped) - the
       * planner times a hurl slide at the projectile's impact. Absent = no
       * shove (keystone unowned, boss, killed target, or a fully-blocked or
       * degenerate shove). */
      knockedTo?: ArenaFxTile;
      /** The archer's Eagle Eye crit (PRD #446 Slice 1, ADR-0237): a critical
       * arrow, so the client can punch up its impact. Absent = an ordinary hit
       * (and every fire-mage projectile - the mage has no crit). */
      crit?: boolean;
    }
  /** A chain hop: a child projectile leaping from a struck enemy to the next. */
  | {
      kind: 'chain';
      projectile: ArenaProjectileKind;
      sizeTier: number;
      from: ArenaFxTile;
      to: ArenaFxTile;
      /** The enemy the hop leaves (may be dead by render time - use the tiles). */
      fromEnemy: EntityId;
      target: EntityId;
      damage: number;
      gen: number;
    }
  /** An on-hit detonation around a struck enemy's tile. */
  | {
      kind: 'explosion';
      at: ArenaFxTile;
      /** Blast radius in tiles (area-stat scaled, for the render footprint). */
      radius: number;
      /** Splash damage dealt to each of `targets`. */
      damage: number;
      targets: EntityId[];
      gen: number;
      /** Cataclysm keystone (PRD #378): render the big blast strip. */
      big?: boolean;
    }
  /** A flame-strike column landing on a struck tile (secondary-hit proc). */
  | {
      kind: 'flame-strike';
      at: ArenaFxTile;
      radius: number;
      damage: number;
      targets: EntityId[];
      gen: number;
    }
  /** Ground catching fire (PRD #378): the client loops a field strip on each
   * tile for `durationMs`. Emitted once per zone at ignition - burn damage is
   * silent by design (the visual is the telegraph). */
  | {
      kind: 'fire-field';
      tiles: ArenaFxTile[];
      durationMs: number;
      gen: number;
      /** The run line's strip orientation (PRD #419): a Scorched Earth trail
       * tile carries the direction the runner departed along, so the flames
       * lie along the trail instead of the parity checkerboard. Absent on a
       * directionless ground patch (Cataclysm) - the client keeps the parity
       * pick as the fallback. */
      orient?: 'ew' | 'ns';
      /** The residue skin (PRD #419 slice B): `molten` draws each tile as a
       * blood-static splat tinted fire instead of the roaring field strips -
       * the Scorched Earth trail and Meteor pools wear it; Cataclysm ground
       * (a fire *wall*) never does. Orthogonal to `orient` (a splat has no
       * direction, so the molten draw ignores it). The archer (ADR-0237) reuses
       * this field visual for its ground effects: `poison` is a Poison Arrows
       * kill-site cloud (a real damage zone), `caltrop` a Caltrops slow tile
       * (a slow marker, not a damage zone). */
      look?: 'molten' | 'poison' | 'caltrop';
    }
  /** Coalesced ground damage presentation; simulation damage is already complete. */
  | {
      kind: 'ground-damage';
      hits: { target: EntityId; damage: number }[];
      gen: number;
    }
  /** Base Nova's bounded circular shockwave. */
  | {
      kind: 'nova';
      at: ArenaFxTile;
      radius: number;
      damage: number;
      targets: EntityId[];
      gen: number;
    }
  /** A batched **status** tick marker cascade (archer, PRD #446 Slice 1,
   * ADR-0237): tiny per-enemy blips the client sprays where a run-state DoT
   * ticked this interval - a blood spurt over a bleeding enemy today. Batched
   * (one event carries every mark this tick) so a wide bleed field costs one
   * event, not one per enemy (the fire-field batched-tiles precedent); floats
   * NO damage numbers, exactly like a zone burn. Purely cosmetic - the damage
   * already landed through the combat seam. */
  | {
      kind: 'status';
      marks: { x: number; y: number; status: 'bleed' | 'poison' | 'slow' }[];
      gen: number;
    }
  /** One ray of a nova ring (PRD #378): a radial flight from the runner's tile
   * to the ray's end, striking `targets` on the way (empty = a pure visual;
   * several = the Serpent Spiral keystone's pierce). `rayIndex` staggers the
   * ring into a spiral client-side. */
  | {
      kind: 'nova-ray';
      projectile: ArenaProjectileKind;
      sizeTier: number;
      from: ArenaFxTile;
      to: ArenaFxTile;
      targets: EntityId[];
      damage: number;
      rayIndex: number;
      gen: number;
    }
  /** A delayed-hit warning (PRD #378 story 14): the client loops the sparkle
   * strip at the tile for `durationMs` - a meteor's whole fall time. */
  | {
      kind: 'telegraph';
      at: ArenaFxTile;
      durationMs: number;
      gen: number;
    }
  /** A meteor landing (Meteor keystone): emitted at the IMPACT tick, after its
   * `telegraph` warned the tile - the star falls and detonates client-side,
   * with the damage already applied sim-side like every other event. */
  | {
      kind: 'meteor';
      at: ArenaFxTile;
      radius: number;
      damage: number;
      targets: EntityId[];
      gen: number;
    }
  /** A Flame Vortex materializing (or dissipating - the same swirl reads both
   * ways): the one-shot vortex flash at the tile (PRD #378). */
  | {
      kind: 'vortex-summon';
      at: ArenaFxTile;
      gen: number;
    }
  /** A Combustion detonation (Scorched Earth keystone, PRD #419 slice C): a
   * corpse that died burning explodes in a mushroom cloud at its tile,
   * splashing `damage` (a fraction of ITS OWN table-max HP) to each of
   * `targets`. Rides whichever beat carried the killing blow - a volley's
   * `skill-fire` cascade (behind the primary flight, never `events[0]`) or a
   * `ground-fx` beat (zone burn / meteor / summon bite). Past the event cap
   * the mushroom fizzles while the damage stands (the graceful-trim contract). */
  | {
      kind: 'combustion';
      at: ArenaFxTile;
      /** Detonation radius in tiles (area-stat scaled, for the render footprint). */
      radius: number;
      damage: number;
      targets: EntityId[];
      gen: number;
    }
  /** One ray of a Splinter burst (More Projectiles keystone, PRD #419 slice D):
   * a radial sub-arrow from the STRUCK tile of a primary projectile hit to the
   * ray's end, striking at most the first live enemy on its walk (empty = a
   * pure visual - the burst is the spectacle, the nova-ray rule). Appended
   * after its parent projectile's whole cascade; `rayIndex` staggers the burst
   * into a spiral client-side, launched at the PARENT's impact instant (unlike
   * a `nova-ray`, which launches with the volley). Always drawn as magic-arrow
   * shrapnel at size 0, so it carries no projectile/sizeTier of its own. */
  | {
      kind: 'splinter';
      from: ArenaFxTile;
      to: ArenaFxTile;
      targets: EntityId[];
      damage: number;
      rayIndex: number;
      gen: number;
    };

/**
 * One theatrical beat the server should enact this tick. The world mutations a
 * beat implies - spawning the enemies, downing the runner - have **already**
 * been applied by {@link stepArena}; a beat is the broadcast/presentation cue,
 * not a request (the {@link ./storm.StormBeat} contract).
 */
export type ArenaBeat =
  /** A regular wave just entered off the spawn ring (its members' ids, for spawn FX). */
  | { kind: 'wave-spawn'; wave: number; enemies: EntityId[] }
  /** An interval elite just entered; cue the elite warning. */
  | { kind: 'elite-spawn'; enemy: EntityId }
  /** A boss milestone: the Demon Knight has entered the arena (ADR-0210: recurring). */
  | { kind: 'boss-spawn'; enemy: EntityId; name: string; herald: string }
  /** An enemy died since last tick (killed through the combat seam); play its die clip, then despawn it. */
  | { kind: 'enemy-die'; enemy: EntityId }
  /**
   * The skill volleyed this tick: the whole cascade, in depth-first order, for
   * FX + damage floats, plus the authoritative **cast facing** the server turned
   * the caster toward (ADR-0192) so the client can mirror the turn. This field
   * is load-bearing for a plain **straight-shot** volley: that path spawns
   * traveling projectile *entities* and emits **no** projectile FX event (Slice
   * D's FX inversion), so `volleyFacing(events)` finds nothing to derive from and
   * the client would never mirror the turn - leaving the walker's facing
   * divergent from the server's and corrupting the walk gate (the "funky, laggy"
   * opening). `null` = no turn (same-tile target: keep the old facing).
   */
  | { kind: 'skill-fire'; source: EntityId; events: ArenaFxEvent[]; castFacing: Direction | null }
  /** Standalone FX outside a volley (a Scorched Earth trail tile igniting, a
   * meteor landing): the same owner-only `ArenaFx` wire, minus the cast clip. */
  | { kind: 'ground-fx'; events: ArenaFxEvent[] }
  /** A summoned Flame Vortex landed a bite (PRD #378): play its swing clip +
   * the damage float (already applied sim-side, the HordeEffect contract). */
  | { kind: 'vortex-strike'; vortex: EntityId; target: EntityId; clip: string; damage: number }
  /** The runner levelled up; a draft is pending iff the run flipped to `paused-draft`. */
  | { kind: 'level-up'; level: number }
  /** The runner went down - the run is over; cue the death/results flow. (The
   * arena is endless, ADR-0210, so this is the *only* way a run ends.) */
  | { kind: 'player-down' };

/** What one {@link stepArena} produced: the beats + bite cues to enact, and whether the run ended this tick. */
export interface ArenaStep {
  beats: ArenaBeat[];
  /** The horde bites landed this tick ({@link ./horde.HordeEffect}), for swing/hit broadcasts. */
  effects: HordeEffect[];
  /**
   * Traveling-projectile contacts landed this tick (PRD #399 Slice C), already
   * applied through the shared combat seam - the server floats a damage number
   * over each struck (still-live) enemy sprite; a `dead` contact is finished by
   * the next-tick reaper (`enemy-die`), so no death handling belongs here.
   */
  projectileHits: ProjectileHit[];
  /** `true` on the single tick the run ends (payout + teardown are the server's to do). */
  ended: boolean;
}

/** Spawn slots left this tick: the run's captured cap minus the live field,
 * minus the boss's reserved seat until it arrives. */
function spawnCapacity(state: ArenaRunState): number {
  const cap = state.bossSpawned ? state.enemyCap : state.enemyCap - ARENA_BOSS_RESERVE;
  return Math.max(0, cap - state.enemies.size);
}

/**
 * Advance the run's choreography by one tick. In order:
 *
 *  1. **Reap the dead.** Any live-set enemy now at zero health (Slice C's
 *     weapons kill through the shared combat seam) - or despawned outright - is
 *     pruned from the live set and its brain dropped; a real death tallies a
 *     kill and emits `enemy-die` (the server plays the die clip and despawns).
 *  2. **Track boss progress** while the current boss lives (fraction gone).
 *  3. **Step the horde** ({@link ./horde.stepHordePursuit}) - chase + bites. A
 *     bite that downs the runner (or a runner found dead/vanished by any other
 *     means) ends the run immediately: `player-down`, status `ended`,
 *     outcome `died`. The arena is **endless** (ADR-0210) - this is the only end.
 *  5. **Waves** on the {@link ARENA_WAVE_INTERVAL_TICKS} cadence, sized by the
 *     current roster tier and clamped to the {@link ARENA_ENEMY_CAP spawn capacity}.
 *  6. **Elites** on the {@link ARENA_ELITE_INTERVAL_TICKS} cadence, deferred
 *     tick-by-tick while the field is at capacity.
 *  7. **The boss** from {@link ARENA_BOSS_TICKS} on, recurring every
 *     {@link ARENA_BOSS_INTERVAL_TICKS} (ADR-0210) but only one alive at a time,
 *     stat-escalated per prior kill; the {@link ARENA_BOSS_RESERVE reserved slot}
 *     guarantees the first entrance.
 *
 * A no-op returning nothing while `paused-draft` (the whole point of the pause)
 * or once `ended`. Deterministic under a seeded world: all randomness draws
 * from `world.rng`, and time only enters as calls to this function.
 */
/**
 * Roll the **Vacuum** drop for a just-reaped elite/boss (PRD #431 Slice B). A
 * boss always drops; an elite drops with {@link ARENA_VACUUM_ELITE_CHANCE},
 * drawn from the seeded run rng (so drops replay deterministically). The roll is
 * **skipped entirely at {@link ARENA_VACUUM_FIELD_CAP}** - a second vacuum never
 * spawns while one lies uncollected, and no rng is drawn (scarcity is the
 * point; the jackpot must stay rare). A regular kill (neither boss nor elite)
 * never drops one.
 */
function maybeDropVacuum(
  world: World,
  state: ArenaRunState,
  at: { x: number; y: number; z?: number },
  boss: boolean,
  elite: boolean,
): void {
  if (!boss && !elite) return;
  if (state.vacuums.length >= ARENA_VACUUM_FIELD_CAP) return;
  if (!boss && world.rng.next() >= ARENA_VACUUM_ELITE_CHANCE) return;
  spawnVacuum(world, state.vacuums, at);
}

export function stepArena(world: World, state: ArenaRunState): ArenaStep {
  const beats: ArenaBeat[] = [];
  let effects: HordeEffect[] = [];
  const projectileHits: ProjectileHit[] = [];
  if (state.status !== 'running') return { beats, effects, projectileHits, ended: false };

  state.elapsedTicks += 1;
  const e = state.elapsedTicks;

  // 1. Reap enemies downed (or despawned) since last tick.
  for (const id of [...state.enemies]) {
    const enemy = world.get(id);
    if (enemy && !isDead(enemy)) continue;
    state.enemies.delete(id);
    state.pursuit.brains.delete(id);
    // Reap-time tier: whether this reaped id was the boss or an interval elite
    // (Set.delete returns true iff it was present) - drives the Vacuum drop
    // roll below (PRD #431 Slice B).
    const wasBoss = state.boss === id;
    const wasElite = state.elites.delete(id);
    if (wasBoss) {
      state.boss = null;
      // Bank this boss's full bar into the cumulative tally and clear the
      // current-boss progress, ready for the next milestone (ADR-0210).
      if (enemy) {
        state.bossKills += 1;
        state.bossProgress = 0;
      }
    }
    if (enemy) {
      // Still in the world at zero health: a real kill this tick. (A missing
      // entity was despawned by the server after an earlier reap - not a new kill.)
      state.kills += 1;
      // The kill drops its tier XP as a ground gem at the corpse tile (VS-style
      // orbs, ./xpGem); the runner earns it by walking into the magnet radius
      // (step 3.4). The xp-gain stat is applied there, at pickup.
      if (enemy.position) {
        spawnXpGem(world, state.gems, enemy.position, state.enemyXp.get(id) ?? 0);
        // The field's biggest fights pay out the field's biggest reward: an
        // elite has a chance, a boss is guaranteed, to drop a Vacuum at the
        // corpse tile (seeded, field-capped) - PRD #431 Slice B.
        maybeDropVacuum(world, state, enemy.position, wasBoss, wasElite);
      }
      beats.push({ kind: 'enemy-die', enemy: id });
    }
    state.enemyXp.delete(id);
  }

  // 2. Boss progress while it lives.
  if (state.boss !== null) {
    const boss = world.get(state.boss);
    if (boss?.health) {
      state.bossProgress = Math.max(state.bossProgress, 1 - boss.health.current / boss.health.max);
    }
  }

  // 2.5. Recycle the outrun (PRD #387 Slice A): running buys space and time
  // between contacts, never escape - the cap stays fully deployed.
  recycleOutrunEnemies(world, state);

  // 3. The horde chases and bites, and any live projectiles fly a tick and
  // COLLIDE against the live enemy set (PRD #399 Slice C). `state.enemies` is the
  // server-supplied live set (the sim cannot tell a player from an NPC - the
  // stepHordePursuit/stepVortexPursuit discipline); each contact applies damage
  // through the shared combat seam and returns a hit the server floats. A killed
  // enemy is reaped next tick by the loop above (it is at zero health now).
  projectileHits.push(...stepProjectiles(world, state.enemies));
  // Combustion (PRD #419 slice C): a traveling-shot kill is a death seam too
  // (the subsystem is dormant since ADR-0216, but every seam must detonate).
  const shotDeaths: Entity[] = [];
  for (const hit of projectileHits) {
    if (!hit.dead) continue;
    const victim = world.get(hit.target);
    if (victim) shotDeaths.push(victim);
  }
  detonateAsGroundFx(world, state, shotDeaths, beats);
  effects = stepHordePursuit(world, state.pursuit, state.player, () => world.rng.next());
  const player = world.get(state.player);
  const playerDown =
    effects.some((f) => f.dead) || !player || isDead(player) || player.death !== undefined;
  if (playerDown) {
    beats.push({ kind: 'player-down' });
    state.status = 'ended';
    state.outcome = 'died';
    return { beats, effects, projectileHits, ended: true };
  }

  // 3.4. XP gems magnetize (VS-style orbs, ./xpGem): every ground gem within
  // the magnet radius of the (live) runner is collected this tick - the whole
  // disc at once, so the client's suck-in animates the pile as a group. XP
  // lands HERE, at pickup, through the xp-gain stat (so a mid-run draft
  // sweetens gems already on the ground); level-ups fall out of checkLevelUps
  // at the end of this same step, exactly as before.
  if (player?.position) {
    // The magnet radius is the runner's live `magnet` stat (PRD #431 Slice A) -
    // read here, at pickup, so a mid-run magnet draft sweeps gems already on
    // the ground exactly as the `xp-gain` stat retroactively sweetens them.
    const radius = arenaMagnetRadius(state.build.stats['magnet'] ?? 0);
    for (const gem of collectXpGems(world, state.gems, player.position, radius)) {
      state.xp += arenaXpGain(gem.value, state.build.stats['xp-gain'] ?? 0);
    }
    // The Vacuum jackpot (PRD #431 Slice B): walking onto a vacuum (its own base
    // disc, unaffected by magnet - you must claim it) hoovers the WHOLE gem
    // field this tick. One extra `collectXpGems(..., Infinity)`; XP banks per
    // gem through `xp-gain` exactly as above, and the multi-level dings resolve
    // through `checkLevelUps` (step 10) one draft at a time. The whole field
    // leaving the world together is what lets the client stage the inrush show.
    if (collectVacuums(world, state.vacuums, player.position).length > 0) {
      for (const gem of collectXpGems(world, state.gems, player.position, Infinity)) {
        state.xp += arenaXpGain(gem.value, state.build.stats['xp-gain'] ?? 0);
      }
    }
  }

  // 3.5. Passive regen (the `regen` draft): town regen stays paused (ADR-0192),
  // so the drafted heal is the runner's only sustain. Every
  // ARENA_REGEN_INTERVAL_TICKS (2s), restore ARENA_REGEN_HP_PER_STAT_LEVEL per
  // drafted level, clamped to the pool - ~0.5 HP/level/second, a top-up not a
  // wall (endless-arena retune). A never-drafted regen (level 0) heals nothing.
  const regenLevel = state.build.stats['regen'] ?? 0;
  if (regenLevel > 0 && e % ARENA_REGEN_INTERVAL_TICKS === 0 && player?.health) {
    player.health.current = Math.min(
      player.health.max,
      player.health.current + ARENA_REGEN_HP_PER_STAT_LEVEL * regenLevel,
    );
  }

  // 4. Burning ground, falling stars, and summoned allies (PRD #378): expire
  // guttered zones and burn every live enemy standing in one, land any due
  // meteors, and step the Flame Vortexes' hunt - all before the volley, so
  // their kills feed the same next-tick reap as any other.
  stepArenaZones(world, state, beats);
  landMeteors(world, state, beats);
  stepFlameVortexes(world, state, beats);
  // Archer entity DoTs / ground slows (ADR-0237): bleeds tick, caltrops slow -
  // before the volley, so their kills feed the same next-tick reap.
  stepArcherStatuses(world, state, beats);

  // 5. The build's skill auto-fires (after the horde's bites: a doomed runner
  // never gets a posthumous volley, and a staged enemy gets its swing in first).
  // Stop-to-cast (ADR-0191): the stillness watch advances first, every running
  // tick - including ticks where the volley is held on cooldown or targets.
  // A tile change also drops the Scorched Earth trail on the departed tile.
  const departed = trackRunnerStillness(state, player);
  if (departed) {
    dropScorchedTrail(state, departed, beats);
    // Caltrops (archer, ADR-0237): scatter a slow tile on the departed tile too.
    dropCaltropTrail(state, departed, beats);
  }
  fireSkill(world, state, beats);

  // 6. The arena is endless (ADR-0210): there is no survival horn - a run ends
  // only when the runner falls (handled above). The horde escalates forever
  // (arenaStageFor's endless tiers) and the score climbs; the goal is to push it
  // as high as possible for the leaderboard.

  // 7. Regular waves, sized by the tier in force, clamped to capacity.
  if (e >= state.nextWaveTick) {
    state.nextWaveTick += ARENA_WAVE_INTERVAL_TICKS;
    const stage = arenaStageFor(e);
    const count = Math.min(stage.waveSize, spawnCapacity(state));
    if (count > 0) {
      state.wave += 1;
      const spawned: EntityId[] = [];
      for (let i = 0; i < count; i++) spawned.push(spawnWaveEnemy(world, state, stage));
      beats.push({ kind: 'wave-spawn', wave: state.wave, enemies: spawned });
    }
  }

  // 8. Interval elites, deferred while the field is at capacity. Progressive
  // elites: the first ARENA_ELITE_FIXED_SPAWNS walk ARENA_ELITE_SEQUENCE two of
  // each in order (a normal-size introduction to the whole elite bestiary,
  // weakest-first); past that each spawn rolls a random type off the run's
  // seeded rng and escalates its stats + sprite scale geometrically.
  if (e >= state.nextEliteTick && spawnCapacity(state) > 0) {
    state.nextEliteTick = e + ARENA_ELITE_INTERVAL_TICKS;
    const eliteSpec = arenaEliteSpawnFor(world, state.eliteSpawnCount);
    state.eliteSpawnCount += 1;
    const eliteAnchor = runnerAnchor(world, state);
    const spot = seededRingSpot(world, eliteAnchor.spot, eliteAnchor.heading);
    const elite = spawnHordeEnemy(world, state.pursuit, {
      x: spot.x,
      y: spot.y,
      facing: seededFacing(world),
      ...monsterAppearance(eliteSpec.monster),
      msPerTile: eliteSpec.msPerTile,
      health: eliteSpec.health,
      damageMin: eliteSpec.damageMin,
      damageMax: eliteSpec.damageMax,
      ...(eliteSpec.scale !== undefined ? { scale: eliteSpec.scale } : {}),
    });
    state.enemies.add(elite.id);
    state.elites.add(elite.id);
    state.enemyXp.set(elite.id, eliteSpec.xp);
    beats.push({ kind: 'elite-spawn', enemy: elite.id });
  }

  // 9. The recurring boss milestone (ADR-0210): a fresh boss every
  // ARENA_BOSS_INTERVAL_TICKS from ARENA_BOSS_TICKS on, but only ever one alive at
  // a time (the next waits for the current to fall - `state.boss === null`), each
  // stat-escalated by the number already defeated so late bosses are genuine walls.
  // The reserved slot (the pre-first-boss `spawnCapacity` hold) still guarantees
  // the opening entrance; the spawn itself is unconditional (it ignores the cap),
  // so later bosses always make it in.
  if (e >= state.nextBossTick && state.boss === null) {
    state.bossSpawned = true;
    state.nextBossTick = e + ARENA_BOSS_INTERVAL_TICKS;
    const tier = state.bossKills; // 0 for the first boss, +1 per one already downed
    const bossAnchor = runnerAnchor(world, state);
    const spot = seededRingSpot(world, bossAnchor.spot, bossAnchor.heading);
    // Rotate the roster by how many have already fallen (ADR-0211): boss 0 is
    // Lord Blackthorn, then Mondain, Minax, ... wrapping. The villain's *name*
    // comes from the roster entry (floated overhead); its *body* from the linked
    // bestiary record (villains may share a sprite).
    const bossDef = ARENA_BOSSES[state.bossKills % ARENA_BOSSES.length]!;
    const bossRecord = monsterById(bossDef.monster)!;
    const { health, damageMin, damageMax, msPerTile, xp } = bossDef;
    const boss = spawnHordeEnemy(world, state.pursuit, {
      x: spot.x,
      y: spot.y,
      facing: seededFacing(world),
      name: bossDef.name,
      body: bossRecord.body,
      bodyHue: bossRecord.hue,
      health: Math.round(health * ARENA_BOSS_HEALTH_GROWTH ** tier),
      damageMin: Math.round(damageMin * ARENA_BOSS_DAMAGE_GROWTH ** tier),
      damageMax: Math.round(damageMax * ARENA_BOSS_DAMAGE_GROWTH ** tier),
      msPerTile,
    });
    state.enemies.add(boss.id);
    state.enemyXp.set(boss.id, Math.round(xp * ARENA_BOSS_HEALTH_GROWTH ** tier));
    state.boss = boss.id;
    // A fresh boss enters at its normal gait; the catch-up edge detector re-arms.
    state.bossCatchup = false;
    beats.push({ kind: 'boss-spawn', enemy: boss.id, name: bossDef.name, herald: bossDef.herald });
  }

  // 10. Level-ups last: crossing a threshold rolls the seeded draft and pauses
  // the run at a whole-tick boundary (the pause freezes the next step, not this one).
  checkLevelUps(world, state, beats);

  return { beats, effects, projectileHits, ended: false };
}

/**
 * Recycle every outrun enemy (PRD #387 Slice A): any live regular/elite enemy
 * beyond {@link ARENA_RECYCLE_RADIUS} of the runner teleports - same entity
 * id, same remaining health, same brain - onto the spawn ring **ahead of the
 * runner's heading** (`frontal`, so the reappearance is where the runner is
 * going, indistinguishable from a fresh spawn). One position delta on the
 * wire; the client already renders a multi-tile jump as a teleport (#379), so
 * this needs zero client work and emits no beat by design.
 *
 * **The boss is exempt**: it never recycles, so the climax chase can be kited
 * but never lost. Instead, beyond the recycle radius it gains the
 * {@link ARENA_BOSS_CATCHUP_FACTOR catch-up gait} (PRD #387 Slice C) -
 * relentless, but always the same entity walking the whole way.
 */
function recycleOutrunEnemies(world: World, state: ArenaRunState): void {
  const player = world.get(state.player);
  if (!player?.position) return;
  const { x: px, y: py, facing } = player.position;
  const runner = { x: px, y: py };
  for (const id of state.enemies) {
    const enemy = world.get(id);
    if (!enemy?.position || !enemy.mobile || isDead(enemy)) continue;
    const beyond =
      chebyshev(enemy.position.x, enemy.position.y, px, py) > ARENA_RECYCLE_RADIUS;
    if (id === state.boss) {
      // The catch-up gait flips with the radius edge: rebuild the mobile at
      // the derived gait (the mobility draft's toState/fromState shape -
      // never compounding, tile/facing/in-flight step preserved).
      if (beyond !== state.bossCatchup) {
        state.bossCatchup = beyond;
        const s = enemy.mobile.toState();
        s.msPerTile = beyond
          ? Math.round(ARENA_BOSS.msPerTile * ARENA_BOSS_CATCHUP_FACTOR)
          : ARENA_BOSS.msPerTile;
        s.msPerTileRun = s.msPerTile / 2;
        enemy.mobile = Mobile.fromState(s);
      }
      continue;
    }
    if (!beyond) continue;
    const spot = seededRingSpot(world, runner, facing, { frontal: true });
    // The hard reposition: mobile snap + position mirror (the movement system
    // mirrors x/y from the mobile, but keep the two agreeing this very tick).
    enemy.mobile.teleport(spot.x, spot.y);
    enemy.position.x = spot.x;
    enemy.position.y = spot.y;
  }
}

// ---------------------------------------------------------------------------
// The auto-fire skill (fire-mage rework, ADR-0179). Movement is the only
// manual input: the one base skill volleys itself on its cooldown from inside
// stepArena - but only while the runner stands still (stop-to-cast,
// ADR-0191): moving holds the shot, so the manual input is really the
// move/plant rhythm. The drafted modifiers cascade each volley
// (multi-projectile fan, on-hit explosions, chain hops, flame-strike procs),
// and all damage flows through the shared combat seam. Deterministic: the only randomness is the
// flame-strike proc roll, drawn from the seeded world rng in fixed
// (depth-first) resolution order.
// ---------------------------------------------------------------------------

/** Chebyshev distance: the range metric combat already uses (king-move adjacency). */
function chebyshev(ax: number, ay: number, bx: number, by: number): number {
  return Math.max(Math.abs(ax - bx), Math.abs(ay - by));
}

/**
 * Advance the stop-to-cast stillness watch (ADR-0191): compare the runner's
 * tile to last tick's and count consecutive same-tile ticks. Tile-based, not
 * mobile-state-based - a predicted client's accepted walks land server-side as
 * instant `mobile.teleport`s (see `gameServer.handleWalk`), so the run-world
 * mobile never reports a step in flight. Turning in place keeps the counter
 * (facing is not position); the counter saturates at the threshold so a long
 * stand can never overflow.
 *
 * Returns the tile the runner **departed** on a tile change (`null` while
 * holding still) - the Scorched Earth trail hook (PRD #378) ignites it.
 */
function trackRunnerStillness(
  state: ArenaRunState,
  player: Entity | undefined,
): ArenaFxTile | null {
  if (!player?.position) return null;
  const { x, y } = player.position;
  if (x === state.lastPlayerTile.x && y === state.lastPlayerTile.y) {
    // Predicted Arena walks land as immediate server teleports. Keep the
    // stop-to-cast gate closed for the step's real gait duration so a one-tick
    // Mobility settle cannot fire in the request gap of continuous movement.
    if (state.elapsedTicks <= state.movingUntilTick) {
      state.stillTicks = 0;
      return null;
    }
    state.stillTicks = Math.min(ARENA_CAST_SETTLE_TICKS, state.stillTicks + 1);
    return null;
  }
  const departed = { x: state.lastPlayerTile.x, y: state.lastPlayerTile.y };
  state.stillTicks = 0;
  state.lastPlayerTile = { x, y };
  return departed;
}

// ---------------------------------------------------------------------------
// Burning ground zones (PRD #378): the Scorched Earth trail and (with the
// Cataclysm keystone) explosion ground both burn through this one machinery.
// ---------------------------------------------------------------------------

/**
 * Register one burning zone and return its ignition `fire-field` event - the
 * zone's whole wire presence (the client loops the strip for the duration);
 * burn damage is silent by design. At the {@link ARENA_MAX_ZONES} cap the
 * OLDEST zone gutters early - a trail-painting runner never loses the tile
 * they just lit. The caller places the event: the trail wraps it in a
 * `ground-fx` beat, a Cataclysm blast pushes it into its volley's cascade so
 * the ground ignites at the blast's landing instant.
 */
function registerArenaZone(
  state: ArenaRunState,
  zone: ArenaZone,
  fx: { orient?: 'ew' | 'ns'; look?: 'molten' | 'poison' } = {},
): ArenaFxEvent {
  state.zones.push(zone);
  if (state.zones.length > ARENA_MAX_ZONES) state.zones.shift();
  const durationMs = (zone.expiresAtTick - state.elapsedTicks) * ARENA_TICK_MS;
  return {
    kind: 'fire-field',
    tiles: zone.tiles.map((t) => ({ ...t })),
    durationMs,
    gen: 0,
    ...(fx.orient !== undefined ? { orient: fx.orient } : {}),
    ...(fx.look !== undefined ? { look: fx.look } : {}),
  };
}

/**
 * Ignite the Scorched Earth trail on the tile the runner just departed (a
 * no-op while unowned). The strip's `orient` follows the step delta (PRD #419:
 * departed -> current, `|dx| >= |dy|` -> east-west) so the flames lie along
 * the run line, and the zone's damage is frozen from the build's **live**
 * projectile damage at drop time - the trail scales with every draft for free.
 */
function dropScorchedTrail(state: ArenaRunState, departed: ArenaFxTile, beats: ArenaBeat[]): void {
  const durationTicks = scorchedTrailDurationTicks(state.build.modifiers);
  if (durationTicks <= 0) return;
  // `lastPlayerTile` is already the CURRENT tile (trackRunnerStillness updated
  // it when it reported the departure this very tick).
  const dx = state.lastPlayerTile.x - departed.x;
  const dy = state.lastPlayerTile.y - departed.y;
  const orient = Math.abs(dx) >= Math.abs(dy) ? 'ew' : 'ns';
  const width = scorchedTrailWidth(state.build.modifiers);
  const half = Math.floor(width / 2);
  const tiles: ArenaFxTile[] = [];
  for (let offset = -half; offset <= half; offset++) {
    // Paint ACROSS travel: horizontal travel gets a north/south barrier.
    tiles.push(orient === 'ew' ? { x: departed.x, y: departed.y + offset } : { x: departed.x + offset, y: departed.y });
  }
  const event = registerArenaZone(
    state,
    {
      tiles,
      expiresAtTick: state.elapsedTicks + durationTicks,
      damage: scorchedTrailDamage(
        state.build.modifiers,
        skillProjectileDamage(
          state.build.modifiers,
          state.build.stats.damage ?? 0,
          state.build.ascensions.power ?? 0,
        ),
      ),
    },
    // Molten residue skin (PRD #419 slice B): the trail splats, never strips.
    { orient, look: 'molten' },
  );
  beats.push({ kind: 'ground-fx', events: [event] });
}

/** Every tile within Chebyshev `radius` of `at` - a burning patch's footprint. */
function tilesWithin(at: ArenaFxTile, radius: number): ArenaFxTile[] {
  const tiles: ArenaFxTile[] = [];
  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) tiles.push({ x: at.x + dx, y: at.y + dy });
  }
  return tiles;
}

/**
 * The densest-cluster aim of a meteor (PRD #378): the live enemy tile with the
 * most live enemies within {@link ARENA_METEOR_RADIUS}, ties broken by live-set
 * insertion order (spawn order - the volley-targeting rule, so no rng).
 * Falls back to the proccing tile over an empty field.
 */
function densestClusterTile(world: World, state: ArenaRunState, fallback: ArenaFxTile): ArenaFxTile {
  const live: ArenaFxTile[] = [];
  for (const id of state.enemies) {
    const enemy = world.get(id);
    if (!enemy?.position || isDead(enemy)) continue;
    live.push({ x: enemy.position.x, y: enemy.position.y });
  }
  let best: ArenaFxTile | null = null;
  let bestCount = 0;
  for (const center of live) {
    let count = 0;
    for (const other of live) {
      if (chebyshev(center.x, center.y, other.x, other.y) <= ARENA_METEOR_RADIUS) count += 1;
    }
    if (count > bestCount) {
      best = center;
      bestCount = count;
    }
  }
  return best ?? fallback;
}

/**
 * Summon a Flame Vortex at the runner's feet (PRD #378): the energy-vortex
 * body painted the fire hue, at the storm vortex's gait, with NO health (the
 * summon is unkillable scenery to the horde, exactly like its storm cousin is
 * to players - the no-health rule IS the mechanism). Registered with a
 * lifetime; {@link stepFlameVortexes} hunts and eventually dissipates it.
 */
function summonFlameVortex(world: World, state: ArenaRunState, beats: ArenaBeat[]): void {
  const player = world.get(state.player);
  if (!player?.position) return;
  const { x, y, facing } = player.position;
  const vortex = world.createEntity({
    position: { x, y, facing },
    mobile: new Mobile({ x, y, facing, msPerTile: ARENA_FLAME_VORTEX_MS_PER_TILE }),
    renderable: { body: ENERGY_VORTEX_BODY, bodyHue: ARENA_FLAME_VORTEX_HUE },
    vortex: true,
    // The replicated interaction role (D5): the summon is scenery like its storm cousin.
    interaction: { role: 'scenery' },
  });
  state.vortexes.push({
    id: vortex.id,
    expiresAtTick: state.elapsedTicks + ARENA_FLAME_VORTEX_LIFETIME_TICKS,
  });
  beats.push({ kind: 'ground-fx', events: [{ kind: 'vortex-summon', at: { x, y }, gen: 0 }] });
}

/**
 * Advance every summoned Flame Vortex (PRD #378): dissipate the expired
 * (removed outright - a whirlwind leaves no corpse; the same swirl flash marks
 * the vanish), then step the shared storm pursuit brain with the target set
 * FLIPPED onto the live horde. Bites damage through the shared combat seam
 * with `enactDeathOnKill: false` (a downed monster dies plainly for the reap,
 * scoring the runner's kill + XP like any volley kill) and surface as
 * `vortex-strike` beats.
 */
function stepFlameVortexes(world: World, state: ArenaRunState, beats: ArenaBeat[]): void {
  if (state.vortexes.length === 0) return;
  const e = state.elapsedTicks;
  for (const summon of state.vortexes) {
    if (summon.expiresAtTick > e) continue;
    const vortex = world.get(summon.id);
    if (vortex?.position) {
      beats.push({
        kind: 'ground-fx',
        events: [
          { kind: 'vortex-summon', at: { x: vortex.position.x, y: vortex.position.y }, gen: 0 },
        ],
      });
    }
    world.remove(summon.id);
  }
  state.vortexes = state.vortexes.filter((s) => s.expiresAtTick > e);
  if (state.vortexes.length === 0) return;
  const effects = stepVortexPursuit(
    world,
    state.vortexPursuit,
    state.enemies,
    () => world.rng.next(),
    ARENA_FLAME_VORTEX_DAMAGE,
    { enactDeathOnKill: false },
  );
  const biteDeaths: Entity[] = [];
  for (const effect of effects) {
    beats.push({
      kind: 'vortex-strike',
      vortex: effect.vortex,
      target: effect.target,
      clip: effect.clip,
      damage: effect.damage,
    });
    if (effect.dead) {
      const victim = world.get(effect.target);
      if (victim) biteDeaths.push(victim);
    }
  }
  // Combustion (PRD #419 slice C): a summon bite is a death seam like any other.
  detonateAsGroundFx(world, state, biteDeaths, beats);
}

/**
 * Land every meteor whose fall completed this tick (Meteor keystone): damage
 * everything within its radius through the shared combat seam, then emit the
 * one `meteor` impact event as a `ground-fx` beat (its `telegraph` played from
 * the proccing volley's cascade {@link ARENA_METEOR_DELAY_TICKS} ago).
 */
function landMeteors(world: World, state: ArenaRunState, beats: ArenaBeat[]): void {
  if (state.pendingMeteors.length === 0) return;
  const e = state.elapsedTicks;
  const due = state.pendingMeteors.filter((m) => m.impactAtTick <= e);
  if (due.length === 0) return;
  state.pendingMeteors = state.pendingMeteors.filter((m) => m.impactAtTick > e);
  for (const meteor of due) {
    const victims = enemiesNear(world, state, meteor.at, ARENA_METEOR_RADIUS, null);
    const deaths: Entity[] = [];
    for (const victim of victims) {
      if (applyDamage(victim, meteor.damage).dead) deaths.push(victim);
    }
    // The impact leaves a molten burn pool (PRD #419 slice B): a tight radius-1
    // zone through the shared burning-ground machinery, its damage a fraction
    // of the impact. Its ignition event rides the meteor's own beat so the
    // client splats the residue at the detonation instant.
    const pool = registerArenaZone(
      state,
      {
        tiles: tilesWithin(meteor.at, ARENA_METEOR_POOL_RADIUS),
        expiresAtTick: state.elapsedTicks + ARENA_METEOR_POOL_TICKS,
        damage: meteorPoolDamage(meteor.damage),
      },
      { look: 'molten' },
    );
    const events: ArenaFxEvent[] = [
      {
        kind: 'meteor',
        at: { ...meteor.at },
        radius: ARENA_METEOR_RADIUS,
        damage: meteor.damage,
        targets: victims.map((v) => v.id),
        gen: 0,
      },
      pool,
    ];
    // Combustion (PRD #419 slice C): a meteor kill of a burning enemy
    // detonates, the mushrooms riding the impact's own beat behind the pool.
    detonateBurningDeaths(world, state, deaths, events, 1);
    beats.push({ kind: 'ground-fx', events });
  }
}

/**
 * Expire guttered zones, then burn - through the shared combat seam, so a zone
 * kill is reaped (and XP'd) exactly like a volley kill. Two burn triggers
 * (PRD #419):
 *
 *  - **Entry** (checked every tick, not just the interval tick): an enemy whose
 *    tile changed onto a burning tile - tracked via the per-enemy
 *    {@link ArenaRunState.enemyLastTiles} record - pays one burn immediately,
 *    so every trail crossing costs regardless of dwell time (a chaser crosses
 *    a one-tile trail in 3-6 ticks; the 10-tick interval alone taxed it zero
 *    or one times by phase luck). A mover pays entry INSTEAD of that tick's
 *    interval burn - exactly one burn per crossing, never a phase-dependent
 *    double.
 *  - **Dwell**: a stationary enemy burns on the
 *    {@link ARENA_ZONE_BURN_INTERVAL_TICKS} cadence, as ever (standing still
 *    never re-triggers entry).
 *
 * Either way an enemy in several overlapping zones burns once per zone
 * (stacked fire is hotter - the POE ground-degen rule, and the cheap rule to
 * reason about). The last-tile record is maintained every tick even with no
 * zones alight - a stale record would fake crossings the moment a zone
 * ignites - and pruned as enemies die. Every burn (entry or interval, any
 * zone) also stamps the enemy's **burning window** on the same record
 * (Combustion, PRD #419 slice C), and a burn kill of a burning enemy
 * detonates here, its mushrooms riding one `ground-fx` beat.
 */
function stepArenaZones(world: World, state: ArenaRunState, beats: ArenaBeat[]): void {
  const e = state.elapsedTicks;
  if (state.zones.length > 0) {
    state.zones = state.zones.filter((zone) => zone.expiresAtTick > e);
  }
  const intervalTick = e % ARENA_ZONE_BURN_INTERVAL_TICKS === 0;
  const seen = new Set<EntityId>();
  const deaths: Entity[] = [];
  const groundHits: { target: EntityId; damage: number; order: number }[] = [];
  let enemyOrder = 0;
  for (const id of state.enemies) {
    const enemy = world.get(id);
    if (!enemy?.position || isDead(enemy)) continue;
    seen.add(id);
    const { x, y } = enemy.position;
    const last = state.enemyLastTiles.get(id);
    const entered = last !== undefined && (last.x !== x || last.y !== y);
    if (last === undefined) {
      state.enemyLastTiles.set(id, { x, y });
    } else if (entered) {
      // Mutate in place: the burning window must survive a tile change.
      last.x = x;
      last.y = y;
    }
    if (state.zones.length === 0) continue;
    if (!entered && !intervalTick) continue;
    let burned = false;
    let aggregateDamage = 0;
    for (const zone of state.zones) {
      if (zone.tiles.some((t) => t.x === x && t.y === y)) {
        burned = true;
        aggregateDamage += zone.damage;
      }
    }
    // Any burn - entry or interval, from any zone kind - stamps the burning
    // window (zone-generic by design; only the detonation is keystone-gated).
    if (burned) {
      if (applyDamage(enemy, aggregateDamage).dead) deaths.push(enemy);
      groundHits.push({ target: id, damage: aggregateDamage, order: enemyOrder });
      state.enemyLastTiles.get(id)!.burningUntilTick = e + ARENA_BURNING_WINDOW_TICKS;
    }
    enemyOrder += 1;
  }
  // Prune the record as enemies die/despawn, so the map cannot grow unbounded.
  if (state.enemyLastTiles.size > seen.size) {
    for (const id of [...state.enemyLastTiles.keys()]) {
      if (!seen.has(id)) state.enemyLastTiles.delete(id);
    }
  }
  // Combustion (PRD #419 slice C): a zone burn is a death seam with no volley
  // cascade to ride, so its detonations land on their own ground-fx beat.
  const events: ArenaFxEvent[] = [];
  if (groundHits.length > 0) {
    const hits = groundHits
      .sort((a, b) => b.damage - a.damage || a.order - b.order)
      .slice(0, 24)
      .map(({ target, damage }) => ({ target, damage }));
    events.push({ kind: 'ground-damage', hits, gen: 0 });
  }
  detonateBurningDeaths(world, state, deaths, events, 0);
  if (events.length > 0) beats.push({ kind: 'ground-fx', events });
}

/**
 * Whether `id` still counts as **burning** ({@link ArenaEnemyRecord}): its one
 * window is stamped and not yet lapsed. The window is deleted when a
 * detonation consumes it, which is the whole at-most-once guarantee.
 */
function isBurning(state: ArenaRunState, id: EntityId): boolean {
  const until = state.enemyLastTiles.get(id)?.burningUntilTick;
  return until !== undefined && until >= state.elapsedTicks;
}

/**
 * Detonate every burning corpse among `seeds` (Combustion - the Scorched
 * Earth keystone, PRD #419 slice C), cascading: each detonation splashes
 * {@link combustionDamage} of the **corpse's own table-max HP** (per-corpse
 * and idempotent, never a running value) to every live enemy within
 * {@link combustionRadius} through the shared combat seam, and a neighbor
 * killed by the splash that was itself burning joins the work queue - so a
 * pack on fire chain-detonates while a lone corpse never chains. Resolution
 * is a FIFO queue in death order (splash victims in live-set insertion
 * order), fully deterministic with **no rng**. Each corpse detonates at most
 * once: the detonation consumes its burning window ({@link isBurning}).
 *
 * FX cap, not damage cap: a mushroom event past {@link ARENA_MAX_CASCADE_EVENTS}
 * fizzles deterministically while its splash stands (the graceful-trim
 * contract). A no-op without the keystone - the burning bookkeeping is
 * zone-generic, the payoff is Scorched Earth's.
 */
function detonateBurningDeaths(
  world: World,
  state: ArenaRunState,
  seeds: Entity[],
  events: ArenaFxEvent[],
  gen: number,
): void {
  if (!state.build.keystones.has('scorched-earth')) return;
  const queue = [...seeds];
  while (queue.length > 0) {
    const corpse = queue.shift()!;
    if (!isDead(corpse)) continue; // defensive: only the dead detonate
    // Never burned, window lapsed, or already detonated: no chain off this one.
    if (!isBurning(state, corpse.id)) continue;
    // Consume the window: at most one detonation per corpse, ever.
    delete state.enemyLastTiles.get(corpse.id)!.burningUntilTick;
    if (!corpse.position || !corpse.health) continue;
    const at = { x: corpse.position.x, y: corpse.position.y };
    const damage = combustionDamage(corpse.health.max);
    const radius = combustionRadius(state.build.stats.area ?? 0);
    const victims = enemiesNear(world, state, at, radius, corpse.id);
    for (const victim of victims) {
      // Splash-killed neighbors join the queue; the pop-time burning check
      // decides whether each actually chains (an unburned corpse never does).
      if (applyDamage(victim, damage).dead) queue.push(victim);
    }
    if (events.length < ARENA_MAX_CASCADE_EVENTS) {
      events.push({
        kind: 'combustion',
        at,
        radius,
        damage,
        targets: victims.map((v) => v.id),
        gen,
      });
    }
  }
}

/**
 * Detonate `deaths` outside a volley (zone burn / meteor already-emitted beat
 * shapes aside / summon bite / traveling shot): the mushrooms ride one fresh
 * `ground-fx` beat - there is no cascade to append to. Emits nothing when
 * nothing detonated (keystone unowned, nobody burning).
 */
function detonateAsGroundFx(
  world: World,
  state: ArenaRunState,
  deaths: Entity[],
  beats: ArenaBeat[],
): void {
  if (deaths.length === 0) return;
  const events: ArenaFxEvent[] = [];
  detonateBurningDeaths(world, state, deaths, events, 0);
  if (events.length > 0) beats.push({ kind: 'ground-fx', events });
}

/**
 * Tick the archer's entity DoTs and ground slows (PRD #446 Slice 1, ADR-0237) -
 * called from {@link stepArena} step 4, before the volley, so a bleed/caltrop
 * kill feeds the same next-tick reap. Mirrors {@link stepArenaZones}: prune the
 * expired, act on the live set against a per-tick `seen` set, and prune records
 * as enemies die. A no-op for every non-archer class (all three maps stay empty).
 *
 *  - **Bleeds** (Barbed Arrows) tick `damagePerStack x stacks` through the shared
 *    combat seam on the {@link ARCHER_BLEED_INTERVAL_TICKS} cadence, floating no
 *    numbers (a `status` blood mark instead - the zone-burn silence rule).
 *  - **Caltrops** slow every live enemy standing on a live caltrop tile (its gait
 *    multiplied via {@link ./mobile.Mobile.setSlowFactor}), the slow lifting
 *    {@link ARCHER_CALTROP_SLOW_DURATION_TICKS} after the last contact.
 */
function stepArcherStatuses(world: World, state: ArenaRunState, beats: ArenaBeat[]): void {
  const e = state.elapsedTicks;
  if (state.caltrops.length > 0) {
    state.caltrops = state.caltrops.filter((c) => c.expiresAtTick > e);
  }
  if (state.bleeds.size === 0 && state.slows.size === 0 && state.caltrops.length === 0) return;

  const intervalTick = e % ARCHER_BLEED_INTERVAL_TICKS === 0;
  const slowFactor = caltropSlowFactor(state.build.modifiers);
  const seen = new Set<EntityId>();
  const marks: { x: number; y: number; status: 'bleed' | 'poison' | 'slow' }[] = [];

  for (const id of state.enemies) {
    const enemy = world.get(id);
    if (!enemy?.position || isDead(enemy)) continue;
    seen.add(id);
    const { x, y } = enemy.position;

    // Caltrop slow: standing on a live caltrop tile (re)applies the slow. Checked
    // every tick so it bites promptly; the record's expiry is refreshed on each contact.
    if (state.caltrops.length > 0 && state.caltrops.some((c) => c.x === x && c.y === y)) {
      enemy.mobile?.setSlowFactor(slowFactor);
      state.slows.set(id, { expiresAtTick: e + ARCHER_CALTROP_SLOW_DURATION_TICKS });
    }

    // Bleed tick: expire a clotted wound, else bleed on the interval.
    const bleed = state.bleeds.get(id);
    if (bleed) {
      if (bleed.expiresAtTick <= e) {
        state.bleeds.delete(id);
      } else if (intervalTick) {
        applyDamage(enemy, bleed.damagePerStack * bleed.stacks);
        marks.push({ x, y, status: 'bleed' });
      }
    }
  }

  // Lift expired (or orphaned) caltrop slows, restoring the enemy's gait.
  if (state.slows.size > 0) {
    for (const [id, slow] of state.slows) {
      if (!seen.has(id)) {
        state.slows.delete(id); // enemy gone - its mobile is being torn down anyway
      } else if (slow.expiresAtTick <= e) {
        world.get(id)?.mobile?.setSlowFactor(1);
        state.slows.delete(id);
      }
    }
  }
  // Prune bleed records as enemies die/despawn (the enemyLastTiles discipline).
  if (state.bleeds.size > seen.size) {
    for (const id of [...state.bleeds.keys()]) {
      if (!seen.has(id)) state.bleeds.delete(id);
    }
  }
  // The batched status marks ride one ground-fx beat (cosmetic - damage landed above).
  if (marks.length > 0) {
    beats.push({ kind: 'ground-fx', events: [{ kind: 'status', marks, gen: 0 }] });
  }
}

/**
 * Scatter a Caltrops slow tile on the tile the runner just departed (a no-op
 * while unowned) - the Scorched Earth trail-drop shape, but a slow marker, not a
 * damage zone (so it never joins {@link ArenaRunState.zones}). At the
 * {@link ARCHER_MAX_CALTROPS} cap the oldest is spent early. Its FX is a
 * `caltrop`-look field the client loops for the tile's lifetime.
 */
function dropCaltropTrail(state: ArenaRunState, departed: ArenaFxTile, beats: ArenaBeat[]): void {
  const durationTicks = caltropTileDurationTicks(state.build.modifiers);
  if (durationTicks <= 0) return;
  const expiresAtTick = state.elapsedTicks + durationTicks;
  state.caltrops.push({ x: departed.x, y: departed.y, expiresAtTick });
  if (state.caltrops.length > ARCHER_MAX_CALTROPS) state.caltrops.shift();
  beats.push({
    kind: 'ground-fx',
    events: [
      {
        kind: 'fire-field',
        tiles: [{ ...departed }],
        durationMs: durationTicks * ARENA_TICK_MS,
        gen: 0,
        look: 'caltrop',
      },
    ],
  });
}

/** A live-set enemy that can still be hit: present, positioned, on the runner's level, not already down. */
function targetableEnemies(world: World, state: ArenaRunState, playerZ: number): Entity[] {
  const out: Entity[] = [];
  for (const id of state.enemies) {
    const enemy = world.get(id);
    if (!enemy?.position || isDead(enemy)) continue;
    if ((enemy.position.z ?? 0) !== playerZ) continue;
    out.push(enemy);
  }
  return out;
}

/** An entity's tile, frozen into a plain FX anchor (never a live reference). */
function fxTile(entity: Entity): ArenaFxTile {
  return { x: entity.position!.x, y: entity.position!.y };
}

/**
 * The facing a volley turns its caster toward (ADR-0192): the direction of the
 * FIRST projectile (events[0] is always the primary flight at the nearest
 * target), sign-normalised to one of the 8 facings; `null` when the volley has
 * no projectile or the target shares the caster's tile (keep the old facing).
 *
 * This is the ONE derivation both sides of the wire use: {@link fireSkill}
 * turns the authoritative run-world mobile with it, and the client mirrors the
 * same turn from the owner-only `ArenaFx` message (`PredictedWalker.faceCast`).
 * The local player is client-predicted and `observeSnapshot` never drift-snaps
 * on facing alone, so a server-only turn would be invisible - deriving
 * identically from the same events keeps the two mobiles' facings agreeing
 * without any extra wire state. The agreement is cosmetic-only since ADR-0248
 * (every `Walk` carries the client's own turn-vs-step kind, and an applied step
 * re-converges the facing), so a skipped mirror - a MOVING caster, the archer's
 * whole identity - no longer corrupts the walk gate into eating a step.
 */
export function volleyFacing(events: readonly ArenaFxEvent[]): Direction | null {
  const first = events[0];
  if (!first || first.kind !== 'projectile') return null;
  return directionForDelta(first.to.x - first.from.x, first.to.y - first.from.y);
}

/**
 * Volley the base skill if it is off cooldown, cascading the drafted modifiers
 * (fire-mage rework, ADR-0179). Targeting is deterministic: candidates sort by
 * Chebyshev distance with ties broken by live-set insertion order (spawn
 * order), `skillProjectileCount` projectiles fan across the nearest distinct
 * targets (surplus projectiles round-robin back onto them), and each
 * projectile's hit resolves depth-first through {@link resolveSkillHit} -
 * explosion, flame-strike proc, then chain hops. A ready skill with **no
 * target in reach holds its shot** (cooldown stays consumed-and-ready) rather
 * than firing into the air, so the first enemy to close always eats a full
 * volley - and a **moving runner holds it the same way** (stop-to-cast,
 * ADR-0191: fires only at {@link ARENA_CAST_SETTLE_TICKS} settled ticks).
 *
 * The whole cascade lands as one `skill-fire` beat whose {@link ArenaFxEvent}
 * list the server forwards verbatim (the owner-only `ArenaFx` message); every
 * point of damage has already been applied through the shared combat seam, and
 * deaths surface as next tick's reap (`enemy-die`), exactly like any other
 * combat-seam kill. The event list (and with it the cascade's growth) is
 * hard-capped at {@link ARENA_MAX_CASCADE_EVENTS}.
 */
function fireSkill(world: World, state: ArenaRunState, beats: ArenaBeat[]): void {
  const player = world.get(state.player);
  if (!player?.position) return;
  const build = state.build;
  const stats = build.stats;

  if (build.skillCooldown > 0) {
    build.skillCooldown -= 1;
    if (build.skillCooldown > 0) return;
  }

  // Stop-to-cast (ADR-0191), now a class-def policy (PRD #446): a moving runner
  // holds the shot exactly like the no-target hold - consumed-and-ready, firing
  // on the first settled tick - UNLESS the class fires while moving (the archer
  // alone, `firesWhileMoving: true`). The fire mage keeps the gate.
  const settleTicks = arenaCastSettleTicks(
    ARENA_CAST_SETTLE_TICKS,
    stats.mobility ?? 0,
    state.classDef.skill.firesWhileMoving,
  );
  if (state.stillTicks < settleTicks) return;

  const px = player.position.x;
  const py = player.position.y;
  const pz = player.position.z ?? 0;
  const range = skillRange(stats.area ?? 0, state.classDef.skill.range);
  // Nearest-first, spawn-order tiebreak: targetableEnemies iterates the live
  // set in insertion order and the sort is stable, so resolution is
  // deterministic without any rng.
  const candidates = targetableEnemies(world, state, pz)
    .map((enemy) => ({ enemy, dist: chebyshev(px, py, enemy.position!.x, enemy.position!.y) }))
    .filter((c) => c.dist <= range)
    .sort((a, b) => a.dist - b.dist)
    .map((c) => c.enemy);
  if (candidates.length === 0) return; // hold the shot until something is in reach

  build.skillCooldown = skillCooldownTicks(
    stats.cooldown ?? 0,
    build.ascensions.alacrity ?? 0,
    state.classDef.skill.cooldownTicks,
  );

  const from = fxTile(player);
  // Per-class cascade dispatch (PRD #446): the shared prelude above (cooldown,
  // the firesWhileMoving gate, nearest-first candidate acquisition, cooldown
  // re-arm) and the tail below (face the volley + push the skill-fire beat) are
  // class-agnostic; only the event-building cascade differs. The fire mage's
  // whole cascade is byte-identical (guarded by the replay pin) - it just moved
  // behind this branch. The archer builds a leaner precision cascade.
  const events: ArenaFxEvent[] = [];
  if (state.classDef.id === 'archer') {
    buildArcherVolley(world, state, candidates, from, events);
  } else {
    buildFireMageVolley(world, state, player, candidates, from, pz, range, events);
  }

  // Face the volley (ADR-0192): snap the caster toward the primary flight so the
  // shot clip plays in the direction the projectile flies (every volley carries
  // a leading event - the tile-resolved reversion, ADR-0216). Same-tile target
  // keeps the old facing. Safe on the run-world mobile (teleport-driven).
  const castDir = volleyFacing(events);
  if (castDir !== null) {
    player.mobile?.face(castDir);
    player.position.facing = castDir;
  }

  beats.push({ kind: 'skill-fire', source: player.id, events, castFacing: castDir });
}

/**
 * The fire mage's firing cascade (PRD #334 / #378 / #419), extracted verbatim
 * behind the class dispatch (PRD #446 Slice 1) - the multi-projectile volley
 * with the Splinter burst, Comet shove, per-hit {@link resolveSkillHit} cascade
 * (explosion / flame-strike / chain / combustion), and the nova ring. Behaviour
 * is byte-identical to the pre-refactor `fireSkill` body (the replay pin
 * guards it); it only reads its scalars off the fire constants as it always did.
 */
function buildFireMageVolley(
  world: World,
  state: ArenaRunState,
  player: Entity,
  candidates: Entity[],
  from: ArenaFxTile,
  pz: number,
  range: number,
  events: ArenaFxEvent[],
): void {
  const build = state.build;
  const stats = build.stats;
  const mods = build.modifiers;
  const keystones = build.keystones;
  const count = skillProjectileCount(mods);
  const kind = skillProjectileKind(mods);
  const sizeTier = skillProjectileSizeTier(mods);
  const damage = skillProjectileDamage(mods, stats.damage ?? 0, build.ascensions.power ?? 0);
  // Splinter (the More Projectiles keystone, PRD #419 slice D): every PRIMARY
  // projectile hit bursts into 8 radial sub-arrows from its struck tile - and
  // ONLY a primary hit (never chain hops, nova rays, pierced extras, or a
  // splinter hit itself), which is why the burst lives here on the volley path
  // and not inside resolveSkillHit.
  const splinterReach = Math.floor(splinterRange(keystones, stats.area ?? 0));
  const splinterDmg = splinterDamage(damage);

  // The skill volley is TILE-RESOLVED (ADR-0216): it locks the nearest enemies and
  // resolves each hit at fire time through the shared combat seam, emitting one
  // `projectile` FX event per shot that the client draws as a tracer flying to -
  // and homing onto - the struck enemy. This reverts the bullet-hell traveling
  // straight-shot entity (PRD #399 Slices B-D) for the BASIC weapon: a guaranteed
  // hit + a visual that lands on the enemy reads better than a dodgeable bolt that
  // whiffs and (a static billboard) pointed the wrong way. The cascade shapes
  // (Fire Serpent pierce, explosion splash, chain hops, flame-strike proc) always
  // resolved this way; the plain shot now joins them, so there is one firing path.
  // The traveling-projectile subsystem (`spawnProjectile`/`stepProjectiles`/
  // `ProjectileSprite`) is retained but dormant - no volley spawns a bolt now.
  for (let i = 0; i < count; i++) {
    // N distinct nearest targets; surplus projectiles round-robin back onto them.
    const target = candidates[i % candidates.length]!;
    // NO loop-top cap break here (ADR-0292): the cap trims CASCADE/cosmetic
    // appends only, never a PRIMARY projectile. A break here would exit before a
    // later projectile pushes its gen-0 flight and resolves its hit, dropping the
    // shot's whole damage once an earlier projectile's rayless splinter/mushroom
    // spam crossed the cap (issue #444) - contradicting the graceful-trim
    // contract ("damage applies in full", ADR-0226; "primary flights always
    // render", ADR-0222). Every gen-0 flight + hit is now unconditional; the
    // per-append cap checks inside resolveSkillHit/emitSplinterBurst/fireNova
    // still cap the cascade. `count` is small (<=6), so this adds at most `count`
    // primary flights past the cap - the intended trim, damage never lost.
    // A projectile is "secondary" for the flame-strike proc iff it is not the
    // volley's first - the user-facing "extra projectile" rule.
    if (kind === 'fire-snake') {
      // The pierce volley path (PRD #378, ex Fire Serpent): the flight runs
      // through the horde - every candidate on the ray through the aimed
      // target eats the FULL hit. The aimed target resolves as the
      // projectile's own hit (chains cascade from it); the run-through extras
      // resolve as secondary gen-1 hits (explosions and procs fire, chains do
      // not re-root), all sharing one hitSet so the primary's chain never
      // re-strikes a skewered enemy. DORMANT since Comet replaced the Fire
      // Serpent keystone (PRD #419 slice E): `skillProjectileKind` no longer
      // returns `fire-snake`, so no volley reaches here - the machinery stays
      // (the traveling-projectile-subsystem precedent) because Serpent
      // Spiral's nova rays still pierce through the same shared math.
      const targetTile = fxTile(target);
      const pierced = candidates.filter(
        (c) =>
          c.id !== target.id &&
          isOnPierceRay(from, targetTile, { x: c.position!.x, y: c.position!.y }, range),
      );
      events.push({
        kind: 'projectile',
        projectile: kind,
        sizeTier,
        from,
        to: pierceRayEnd(from, targetTile, range),
        target: target.id,
        damage,
        gen: 0,
        pierced: pierced.map((p) => p.id),
      });
      // Splinter victims are collected BEFORE the hit resolves (the pierced
      // rule) and seed the shared hitSet: the primary's chain never re-strikes
      // a ray victim, and a skewered extra - which already ate the full hit -
      // is never re-struck by a ray. The burst radiates from the AIMED target
      // only (pierced extras are gen-1 hits, and gen-1 never bursts).
      const splinterRays = collectSplinterRays(
        world,
        state,
        targetTile,
        splinterReach,
        pz,
        new Set([target.id, ...pierced.map((p) => p.id)]),
      );
      const hitSet = new Set([
        target.id,
        ...pierced.map((p) => p.id),
        ...splinterRays.flatMap((r) => (r.victim ? [r.victim.id] : [])),
      ]);
      resolveSkillHit(world, state, events, target, damage, i > 0, 0, hitSet);
      for (const enemy of pierced) {
        if (events.length >= ARENA_MAX_CASCADE_EVENTS) break;
        resolveSkillHit(world, state, events, enemy, damage, true, 1, hitSet);
      }
      // The burst appends after the parent projectile's whole cascade, before
      // the next projectile's flight (PRD #419 slice D).
      emitSplinterBurst(world, state, events, targetTile, splinterRays, splinterDmg, hitSet);
      continue;
    }
    const targetTile = fxTile(target);
    const shot: Extract<ArenaFxEvent, { kind: 'projectile' }> = {
      kind: 'projectile',
      projectile: kind,
      sizeTier,
      from,
      to: targetTile,
      target: target.id,
      damage,
      gen: 0,
    };
    events.push(shot);
    // Splinter (PRD #419 slice D): victims collected pre-resolve seed the
    // shared hitSet (the primary's chain never re-strikes a ray victim), the
    // burst itself appends after the parent's cascade. Collected from the
    // STRIKE tile before the Comet shove below relocates the target - the
    // rays radiate from where the projectile hit, independent of the hurl
    // (the two keystones ride different modifier lines, so both can be owned).
    const splinterRays = collectSplinterRays(
      world,
      state,
      targetTile,
      splinterReach,
      pz,
      new Set([target.id]),
    );
    const hitSet = new Set([
      target.id,
      ...splinterRays.flatMap((r) => (r.victim ? [r.victim.id] : [])),
    ]);
    resolveSkillHit(world, state, events, target, damage, i > 0, 0, hitSet);
    emitSplinterBurst(world, state, events, targetTile, splinterRays, splinterDmg, hitSet);
    // Comet (the Greater Projectile keystone, PRD #419 slice E): the PRIMARY
    // hit hurls its struck enemy along the flight line - after the whole hit
    // cascade resolved (the blast/chain/burst geometry anchors on the strike
    // tile; the hurl is the consequence), and only a SURVIVOR of that cascade
    // is shoved (a corpse stays where it fell for the reap and Combustion).
    // Chain hops, nova rays, splinter rays, and pierced extras never shove:
    // the shove lives here on the volley path, never in resolveSkillHit (the
    // Splinter structural rule).
    const landing = applyCometShove(world, state, target, from, targetTile, pz);
    if (landing) shot.knockedTo = landing;
  }

  // Nova (PRD #378): every Nth volley additionally rings radial projectiles
  // out from the runner's tile. Appended AFTER the primary cascade, so
  // events[0] stays the primary flight (the volleyFacing invariant, ADR-0192).
  // A straight-shot build carries no primary event, so nova events (if any) are
  // the whole `events` list - still a valid tile-resolved cascade.
  fireNova(world, state, player, events);
}

/**
 * The **archer's** firing cascade (PRD #446 Slice 1, ADR-0237): a lean precision
 * volley. Per shot (Double Shot fires several at the next-nearest foes) it rolls
 * a crit off the seeded rng (Eagle Eye), lands the hit through the shared combat
 * seam, emits one physical `bow-arrow`/`bolt` flight, then applies its on-hit
 * effects in fixed order: **bleed** (Barbed Arrows - the entity DoT), **ricochet**
 * bounces (chain-hop geometry), and a **poison cloud** at a kill site (Poison
 * Arrows - a ground zone). Caltrops (a slow trail) drops off the runner's
 * movement in step 5, not here. Every rng draw is the last `&&` term, drawn only
 * when the proc is possible - the flame-strike proc discipline, byte-identical
 * per seed. The event list is capped at {@link ARENA_MAX_CASCADE_EVENTS}.
 */
function buildArcherVolley(
  world: World,
  state: ArenaRunState,
  candidates: Entity[],
  from: ArenaFxTile,
  events: ArenaFxEvent[],
): void {
  const build = state.build;
  const stats = build.stats;
  const mods = build.modifiers;
  const count = archerArrowCount(mods);
  const kind = archerProjectileKind(mods);
  const sizeTier = archerProjectileSizeTier(mods);
  const baseDamage = archerArrowDamage(mods, stats.damage ?? 0, build.ascensions.power ?? 0);
  const critChance = archerCritChance(mods);
  const bounces = archerRicochetBounces(mods);
  const bleedLevel = mods['barbed-arrows'] ?? 0;
  const e = state.elapsedTicks;

  for (let i = 0; i < count; i++) {
    const target = candidates[i % candidates.length]!;
    // Crit (Eagle Eye): the rng is drawn ONLY when a crit is possible (chance > 0),
    // as the last `&&` term - fixed draw order keeps seeded replays byte-identical.
    const crit = critChance > 0 && world.rng.next() < critChance;
    const damage = crit ? archerCritDamage(baseDamage) : baseDamage;
    const targetTile = fxTile(target);
    // The arrow flight (item-art, rotated client-side along from->to). One event
    // per shot; a `crit` flag lets the client punch up the impact.
    events.push({
      kind: 'projectile',
      projectile: kind,
      sizeTier,
      from,
      to: targetTile,
      target: target.id,
      damage,
      gen: 0,
      ...(crit ? { crit: true } : {}),
    });
    // Land the primary hit through the shared combat seam.
    const killed = applyDamage(target, damage).dead;
    // Barbed Arrows: deepen the target's bleed wound (the entity DoT). Only a
    // live target keeps a bleed - a corpse is about to be reaped.
    if (bleedLevel > 0 && !killed) {
      const existing = state.bleeds.get(target.id);
      const stacks = Math.min(ARCHER_BLEED_MAX_STACKS, (existing?.stacks ?? 0) + 1);
      state.bleeds.set(target.id, {
        expiresAtTick: e + bleedDurationTicks(bleedLevel),
        damagePerStack: bleedTickDamage(bleedLevel, baseDamage),
        stacks,
      });
    }
    // Ricochet: the arrow bounces onward to the nearest not-yet-hit enemies,
    // each bounce a decayed hit off the PRIMARY (never a running value). Reuses
    // the chain-hop nearest-first geometry; each bounce is its own `chain` FX.
    if (bounces > 0) {
      const hitSet = new Set<EntityId>([target.id]);
      let fromTile = targetTile;
      for (let b = 1; b <= bounces; b++) {
        if (events.length >= ARENA_MAX_CASCADE_EVENTS) break;
        let pick: Entity | null = null;
        let bestDist = Infinity;
        for (const cand of enemiesNear(world, state, fromTile, ARCHER_RICOCHET_RANGE, null)) {
          if (hitSet.has(cand.id)) continue;
          const dist = chebyshev(fromTile.x, fromTile.y, cand.position!.x, cand.position!.y);
          if (dist < bestDist) {
            pick = cand;
            bestDist = dist;
          }
        }
        if (!pick) break; // nothing new in reach - the bounce fizzles
        hitSet.add(pick.id);
        const bounceDamage = archerRicochetDamage(baseDamage, b);
        const bounceKilled = applyDamage(pick, bounceDamage).dead;
        events.push({
          kind: 'chain',
          projectile: kind,
          sizeTier,
          from: fromTile,
          to: fxTile(pick),
          fromEnemy: hitSet.size === 2 ? target.id : pick.id,
          target: pick.id,
          damage: bounceDamage,
          gen: b,
        });
        // A bounce that kills also seeds a poison cloud (Barbed's bleed applies
        // to the primary arrowhead only, never a bounce).
        if (bounceKilled) dropPoisonCloud(state, fxTile(pick), baseDamage, events);
        fromTile = fxTile(pick);
      }
    }
    // Poison Arrows: a killed target bursts into a lingering poison cloud (a
    // ground zone - reuses the Scorched Earth zone machinery).
    if (killed) dropPoisonCloud(state, targetTile, baseDamage, events);
  }
}

/**
 * Set the archer runner's held `weapon`-layer item to match its build (PRD #446
 * Slice 1, ADR-0237): a bow, or a crossbow once Heavy Draw is drafted. Render-only
 * (the halberd precedent) - it swaps the sole `weapon` slot and rides the ordinary
 * renderable snapshot to viewers. Idempotent; a no-op if the look is already right.
 */
function equipArcherWeapon(entity: Entity, mods: ArenaModifierLevels): void {
  const renderable = entity.renderable;
  if (!renderable) return;
  const weaponId = (mods['heavy-draw'] ?? 0) >= 1 ? 'crossbow' : 'bow';
  const current = renderable.equipment ?? [];
  if (current.some((e) => e.layer === 'weapon' && e.id === weaponId)) return; // already set
  renderable.equipment = [
    ...current.filter((e) => e.layer !== 'weapon'),
    { id: weaponId, layer: 'weapon' },
  ];
}

/**
 * Drop a Poison Arrows kill-site cloud (a real damage {@link ArenaZone}) at
 * `at`, its FX riding the passed cascade (a no-op while the modifier is unowned).
 * The cloud's per-interval damage derives from the killing arrow's `sourceDamage`.
 */
function dropPoisonCloud(
  state: ArenaRunState,
  at: ArenaFxTile,
  sourceDamage: number,
  events: ArenaFxEvent[],
): void {
  const durationTicks = poisonCloudDurationTicks(state.build.modifiers);
  if (durationTicks <= 0) return;
  if (events.length >= ARENA_MAX_CASCADE_EVENTS) return;
  const radius = Math.floor(poisonCloudRadius(state.build.modifiers, state.build.stats.area ?? 0));
  const event = registerArenaZone(
    state,
    {
      tiles: tilesWithin(at, radius),
      expiresAtTick: state.elapsedTicks + durationTicks,
      damage: poisonCloudDamage(state.build.modifiers, sourceDamage),
    },
    { look: 'poison' },
  );
  events.push(event);
}

/** The eight ray directions of a nova ring, in fixed clockwise-from-north
 * order (determinism + the client's spiral stagger reads as a rotation). */
const NOVA_RAY_DELTAS: readonly { dx: number; dy: number }[] = [
  { dx: 0, dy: -1 },
  { dx: 1, dy: -1 },
  { dx: 1, dy: 0 },
  { dx: 1, dy: 1 },
  { dx: 0, dy: 1 },
  { dx: -1, dy: 1 },
  { dx: -1, dy: 0 },
  { dx: -1, dy: -1 },
];

/**
 * Ring the nova (PRD #378) if this volley is the Nth: eight radial rays from
 * the runner's tile, each striking the first live enemy on its tile-walk (or
 * EVERY enemy on the ray with the Serpent Spiral keystone), resolved like a
 * primary hit but secondary for flame-strike purposes (the "extra projectile"
 * rule). Rays with nobody on them still ride the wire - the ring is the
 * spectacle - but carry no targets. All eight rays emit even at partial
 * cascade budget; each is cap-checked like every event append.
 */
function fireNova(
  world: World,
  state: ArenaRunState,
  player: Entity,
  events: ArenaFxEvent[],
): void {
  const build = state.build;
  const interval = novaInterval(build.modifiers);
  if (interval <= 0) return;
  build.volleysSinceNova += 1;
  if (build.volleysSinceNova < interval) return;
  build.volleysSinceNova = 0;

  const stats = build.stats;
  const reach = Math.floor(novaRange(build.modifiers, stats.area ?? 0));
  const damage = novaRayDamage(
    skillProjectileDamage(build.modifiers, stats.damage ?? 0, build.ascensions.power ?? 0),
  );
  const spiral = build.keystones.has('nova');
  const projectile: ArenaProjectileKind = spiral
    ? 'fire-snake'
    : skillProjectileKind(build.modifiers);
  const sizeTier = skillProjectileSizeTier(build.modifiers);
  const from = fxTile(player);
  const enemies = targetableEnemies(world, state, player.position!.z ?? 0);

  // The dependable base skill is one circle: every eligible enemy is hit once,
  // with no per-target projectile cascade. Generic burning-death payoffs remain.
  const circleTargets = enemies.filter(
    (enemy) => !isDead(enemy) && chebyshev(from.x, from.y, enemy.position!.x, enemy.position!.y) <= reach,
  );
  const circleDeaths: Entity[] = [];
  for (const enemy of circleTargets) {
    if (applyDamage(enemy, damage).dead) circleDeaths.push(enemy);
  }
  if (events.length < ARENA_MAX_CASCADE_EVENTS) {
    events.push({ kind: 'nova', at: from, radius: reach, damage, targets: circleTargets.map((e) => e.id), gen: 1 });
  }
  detonateBurningDeaths(world, state, circleDeaths, events, 2);
  if (!spiral) return;

  for (let rayIndex = 0; rayIndex < NOVA_RAY_DELTAS.length; rayIndex++) {
    if (events.length >= ARENA_MAX_CASCADE_EVENTS) break;
    const { dx, dy } = NOVA_RAY_DELTAS[rayIndex]!;
    // Tile-walk outward: every live enemy sitting exactly on the ray's tiles,
    // nearest first (several on one tile all count - they are stacked).
    const onRay: Entity[] = [];
    for (let i = 1; i <= reach; i++) {
      const tx = from.x + dx * i;
      const ty = from.y + dy * i;
      for (const enemy of enemies) {
        if (isDead(enemy)) continue; // an earlier ray's cascade may have downed it
        if (enemy.position!.x === tx && enemy.position!.y === ty) onRay.push(enemy);
      }
    }
    const struck = onRay;
    events.push({
      kind: 'nova-ray',
      projectile,
      sizeTier,
      from,
      to: { x: from.x + dx * reach, y: from.y + dy * reach },
      targets: struck.map((s) => s.id),
      damage,
      rayIndex,
      gen: 0,
    });
    const hitSet = new Set(struck.map((s) => s.id));
    for (const [i, enemy] of struck.entries()) {
      if (events.length >= ARENA_MAX_CASCADE_EVENTS) break;
      // The nearest hit is the ray's own (chains cascade from it); spiral
      // pierce extras resolve gen-1 like the Fire Serpent's.
      resolveSkillHit(world, state, events, enemy, damage, true, i === 0 ? 0 : 1, hitSet);
    }
  }
}

/** One planned ray of a Splinter burst: its end tile and the (at most one)
 * live enemy the tile-walk found on it. */
interface SplinterRay {
  end: ArenaFxTile;
  victim: Entity | null;
}

/**
 * Plan a Splinter burst's 8 radial rays out of `origin` - the struck tile of a
 * PRIMARY projectile hit (PRD #419 slice D). Each ray tile-walks outward in
 * {@link NOVA_RAY_DELTAS} order and locks the FIRST live enemy on its tiles
 * (nearest tile first, live-set insertion order breaking a stacked tie - the
 * volley's determinism rule); `exclude` (the projectile's own hits so far: the
 * aimed target, plus skewered extras on a serpent) is never re-struck. The
 * walk starts one tile out, so the origin tile - the primary target and any
 * enemy stacked with it - is always spared (the nova-ray origin rule).
 * Collected BEFORE the primary hit resolves, mirroring the pierce extras, so
 * the victims can seed the shared hitSet. Empty while the keystone is unowned
 * (`reach` 0).
 */
function collectSplinterRays(
  world: World,
  state: ArenaRunState,
  origin: ArenaFxTile,
  reach: number,
  playerZ: number,
  exclude: ReadonlySet<EntityId>,
): SplinterRay[] {
  if (reach <= 0) return [];
  const enemies = targetableEnemies(world, state, playerZ);
  const rays: SplinterRay[] = [];
  for (const { dx, dy } of NOVA_RAY_DELTAS) {
    let victim: Entity | null = null;
    for (let i = 1; i <= reach && victim === null; i++) {
      const tx = origin.x + dx * i;
      const ty = origin.y + dy * i;
      for (const enemy of enemies) {
        if (exclude.has(enemy.id) || isDead(enemy)) continue;
        if (enemy.position!.x === tx && enemy.position!.y === ty) {
          victim = enemy;
          break;
        }
      }
    }
    rays.push({ end: { x: origin.x + dx * reach, y: origin.y + dy * reach }, victim });
  }
  return rays;
}

/**
 * Fire a collected Splinter burst: one `splinter` event per ray (all 8 ride
 * the wire even with nobody on them - the burst is the spectacle, the
 * nova-ray rule), each victim resolved as a gen-1 SECONDARY hit through
 * {@link resolveSkillHit} - explosions and flame-strike procs fire, chains
 * never re-root (gen 1), and the hit never splinters again (bursts exist only
 * on the volley's primary path). Ray events past
 * {@link ARENA_MAX_CASCADE_EVENTS} fizzle while their damage still applies
 * (the graceful-trim contract). A no-op on an empty plan (keystone unowned).
 */
function emitSplinterBurst(
  world: World,
  state: ArenaRunState,
  events: ArenaFxEvent[],
  origin: ArenaFxTile,
  rays: SplinterRay[],
  damage: number,
  hitSet: Set<EntityId>,
): void {
  for (const [rayIndex, ray] of rays.entries()) {
    if (events.length < ARENA_MAX_CASCADE_EVENTS) {
      events.push({
        kind: 'splinter',
        from: { x: origin.x, y: origin.y },
        to: ray.end,
        targets: ray.victim ? [ray.victim.id] : [],
        damage,
        rayIndex,
        gen: 1,
      });
    }
    if (ray.victim) {
      resolveSkillHit(world, state, events, ray.victim, damage, true, 1, hitSet);
    }
  }
}

/**
 * Enact one Comet shove (the Greater Projectile keystone, PRD #419 slice E):
 * hurl `target` {@link cometKnockbackTiles} tiles out of its `strike` tile
 * along the `from` -> `strike` flight line, clamped to the last walkable tile
 * ({@link cometLanding} over the run world's own level-local walkability seam -
 * the same predicate the movement system steps through). The relocation is the
 * hard-reposition pair the recycler uses: `mobile.teleport` + position mirror,
 * so the two agree this very tick and the wire carries one position delta.
 *
 * Deliberately does NOT touch {@link ArenaRunState.enemyLastTiles}: the
 * record stays at the pre-shove tile, so next tick's {@link stepArenaZones}
 * sees the landing as an ordinary tile change - a shove onto a burning tile
 * pays Slice A's entry burn (the trail-crossing combo), while the tiles flown
 * OVER never burn (the landing is the only crossing, by design).
 *
 * Returns the landing tile for the event's `knockedTo`, or `null` when
 * nothing moved: keystone unowned, the boss (its lever is 0 = immune), a dead
 * target (a killing hit never shoves - the corpse stays put), a degenerate
 * same-tile aim, or a first step already blocked.
 */
function applyCometShove(
  world: World,
  state: ArenaRunState,
  target: Entity,
  from: ArenaFxTile,
  strike: ArenaFxTile,
  playerZ: number,
): ArenaFxTile | null {
  const tiles = cometKnockbackTiles(state.build.keystones, target.id === state.boss);
  if (tiles <= 0) return null;
  if (isDead(target) || !target.position || !target.mobile) return null;
  const landing = cometLanding(from, strike, tiles, walkableForLevel(world, playerZ));
  if (!landing) return null;
  target.mobile.teleport(landing.x, landing.y);
  target.position.x = landing.x;
  target.position.y = landing.y;
  return { ...landing };
}

/** Live-set enemies (not already downed) within `radius` tiles of `at`, minus `exclude`. */
function enemiesNear(
  world: World,
  state: ArenaRunState,
  at: ArenaFxTile,
  radius: number,
  exclude: EntityId | null,
): Entity[] {
  const out: Entity[] = [];
  for (const id of state.enemies) {
    if (id === exclude) continue;
    const enemy = world.get(id);
    if (!enemy?.position || isDead(enemy)) continue;
    if (chebyshev(at.x, at.y, enemy.position.x, enemy.position.y) <= radius) out.push(enemy);
  }
  return out;
}

/**
 * Resolve one landed skill hit depth-first: apply the damage, then cascade the
 * on-hit modifiers in a fixed order (explosion -> flame strike -> chain) so
 * event order - and the flame-strike rng draw order - is deterministic.
 *
 *  - **Explosion** splashes every OTHER live enemy in radius (the struck enemy
 *    already ate the direct hit); already-downed enemies are skipped so a
 *    cascade never wastes splash on corpses awaiting the reap.
 *  - **Flame strike** procs only on a *secondary* hit (a chained hit or a
 *    non-first projectile), rolled against the seeded world rng only when the
 *    proc is possible (fixed draw order keeps replays byte-identical); the
 *    column includes the struck enemy - it is bonus damage.
 *  - **Chain** cascades only from primary (gen 0) hits - chained hits never
 *    re-chain - hopping to the nearest live enemy within ARENA_CHAIN_RANGE not
 *    yet struck by THIS projectile's cascade (`hitSet`), each hop's damage
 *    derived from the primary hit (never a running value).
 *
 * Every event append is capped at {@link ARENA_MAX_CASCADE_EVENTS}: past the
 * cap the cascade stops growing (damage already applied stays applied).
 */
function resolveSkillHit(
  world: World,
  state: ArenaRunState,
  events: ArenaFxEvent[],
  target: Entity,
  damage: number,
  secondary: boolean,
  gen: number,
  hitSet: Set<EntityId>,
): void {
  // The shared seam; the reap turns deaths into kills. Fresh corpses collect
  // for the Combustion pass below (PRD #419 slice C).
  const deaths: Entity[] = [];
  if (applyDamage(target, damage).dead) deaths.push(target);
  const mods = state.build.modifiers;
  const stats = state.build.stats;
  const keystones = state.build.keystones;
  const at = fxTile(target);

  // Explosion: splash around the struck enemy (excluded - it ate the direct
  // hit). The Cataclysm keystone grows the blast and leaves burning ground on
  // the tile it struck - the zone's fire-field event rides THIS cascade so the
  // client ignites it at the blast's landing instant.
  const blastRadius = explosionRadius(mods, stats.area ?? 0, keystones);
  if (blastRadius > 0 && events.length < ARENA_MAX_CASCADE_EVENTS) {
    const splash = explosionDamage(damage, mods);
    const victims = enemiesNear(world, state, at, blastRadius, target.id);
    for (const victim of victims) {
      if (applyDamage(victim, splash).dead) deaths.push(victim);
    }
    events.push({
      kind: 'explosion',
      at,
      radius: blastRadius,
      damage: splash,
      targets: victims.map((v) => v.id),
      gen: gen + 1,
      ...(keystones.has('explosion') ? { big: true } : {}),
    });
    if (keystones.has('explosion') && events.length < ARENA_MAX_CASCADE_EVENTS) {
      events.push(
        registerArenaZone(state, {
          tiles: tilesWithin(at, ARENA_CATACLYSM_BURN_RADIUS),
          expiresAtTick: state.elapsedTicks + ARENA_CATACLYSM_BURN_TICKS,
          damage: cataclysmBurnDamage(splash),
        }),
      );
    }
  }

  // Flame strike: a secondary-hit proc, rolled only when it can fire so the
  // seeded draw order is fixed. The Meteor keystone replaces the instant
  // column with a telegraphed star on the densest cluster (PRD #378): the
  // sparkle warning rides this cascade; the impact lands via landMeteors.
  const strikeChance = flameStrikeChance(mods);
  if (
    secondary &&
    strikeChance > 0 &&
    events.length < ARENA_MAX_CASCADE_EVENTS &&
    world.rng.next() < strikeChance
  ) {
    if (keystones.has('flame-strike')) {
      // Past the in-flight cap the proc fizzles - deterministically, since the
      // chance roll above was still drawn.
      if (state.pendingMeteors.length < ARENA_MAX_PENDING_METEORS) {
        const aim = densestClusterTile(world, state, at);
        state.pendingMeteors.push({
          at: aim,
          impactAtTick: state.elapsedTicks + ARENA_METEOR_DELAY_TICKS,
          damage: meteorDamage(damage),
        });
        events.push({
          kind: 'telegraph',
          at: { ...aim },
          durationMs: ARENA_METEOR_DELAY_TICKS * ARENA_TICK_MS,
          gen: gen + 1,
        });
      }
    } else {
      const strikeRadius = flameStrikeRadius(mods, stats.area ?? 0);
      const strike = flameStrikeDamage(damage);
      // The struck enemy is included: the column lands on its tile as bonus damage.
      const victims = enemiesNear(world, state, at, strikeRadius, null);
      for (const victim of victims) {
        if (applyDamage(victim, strike).dead) deaths.push(victim);
      }
      events.push({
        kind: 'flame-strike',
        at,
        radius: strikeRadius,
        damage: strike,
        targets: victims.map((v) => v.id),
        gen: gen + 1,
      });
    }
  }

  // Combustion (the Scorched Earth keystone, PRD #419 slice C): this hit's
  // fresh corpses that died burning detonate now - after the on-hit events
  // (the mushroom reads as a consequence of the blast), before the chain (a
  // combusted neighbor is already dead, so hops route around it). The
  // mushrooms ride THIS volley's cascade, never a separate beat.
  detonateBurningDeaths(world, state, deaths, events, gen + 1);

  // Chain first resolves its complete normal trunk. Fork is strictly additive:
  // after that trunk is fixed, each successful hop may add one weaker leaf.
  if (gen === 0) {
    const hops = chainHops(mods);
    let link = target;
    const forkOrigins: { source: Entity; damage: number }[] = [];
    for (let hop = 1; hop <= hops; hop++) {
      const hopDamage = chainHopDamage(damage, hop, keystones);
      if (events.length >= ARENA_MAX_CASCADE_EVENTS) break;
      const fromTile = fxTile(link);
      let pick: Entity | null = null;
      let bestDist = Infinity;
      for (const candidate of enemiesNear(world, state, fromTile, ARENA_CHAIN_RANGE, null)) {
        if (hitSet.has(candidate.id)) continue;
        const dist = chebyshev(fromTile.x, fromTile.y, candidate.position!.x, candidate.position!.y);
        if (dist < bestDist) { pick = candidate; bestDist = dist; }
      }
      if (!pick) break;
      hitSet.add(pick.id);
      forkOrigins.push({ source: link, damage: hopDamage });
      events.push({ kind: 'chain', projectile: skillProjectileKind(mods), sizeTier: skillProjectileSizeTier(mods), from: fromTile, to: fxTile(pick), fromEnemy: link.id, target: pick.id, damage: hopDamage, gen: hop });
      resolveSkillHit(world, state, events, pick, hopDamage, true, hop, hitSet);
      link = pick;
    }
    if (keystones.has('chain')) {
      for (const origin of forkOrigins) {
        if (events.length >= ARENA_MAX_CASCADE_EVENTS) break;
        const from = fxTile(origin.source);
        let pick: Entity | null = null;
        let bestDist = Infinity;
        for (const candidate of enemiesNear(world, state, from, ARENA_CHAIN_RANGE, null)) {
          if (hitSet.has(candidate.id)) continue;
          const dist = chebyshev(from.x, from.y, candidate.position!.x, candidate.position!.y);
          if (dist < bestDist) { pick = candidate; bestDist = dist; }
        }
        if (!pick) continue;
        hitSet.add(pick.id);
        const branchDamage = forkBranchDamage(origin.damage);
        events.push({ kind: 'chain', projectile: skillProjectileKind(mods), sizeTier: skillProjectileSizeTier(mods), from, to: fxTile(pick), fromEnemy: origin.source.id, target: pick.id, damage: branchDamage, gen: hops + 1 });
        resolveSkillHit(world, state, events, pick, branchDamage, true, 1, hitSet);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// The level-up draft (Slice C, ADR-0175). Seeded, server-authoritative, and
// pausing: crossing an XP threshold consumes it, rolls a deterministic offer
// from the run world's rng, and flips the run to paused-draft until the pick.
// ---------------------------------------------------------------------------

/** How many options a level-up draft offers (fewer only when the pool runs dry). */
export const ARENA_DRAFT_SIZE = 3;

/**
 * The full draft pool for the current build, in a fixed order
 * ({@link ./arenaWeapons.ARENA_MODIFIER_IDS} order for modifiers, then
 * {@link ./arenaWeapons.ARENA_STAT_IDS} order for stats) so a seeded pick is
 * deterministic: every modifier below {@link ./arenaWeapons.ARENA_MODIFIER_MAX_LEVEL},
 * every stat below {@link ./arenaWeapons.ARENA_STAT_MAX_LEVEL}.
 */
function draftPool(
  build: ArenaBuild,
  classDef: ArenaClassDef,
  rules: ArenaChallengeRules,
): ArenaDraftOption[] {
  const pool: ArenaDraftOption[] = [];
  // The class's own modifier lines and keystone map (PRD #446): the fire mage's
  // are the shipped `ARENA_MODIFIER_IDS`/`ARENA_KEYSTONES`, so this is
  // byte-identical today; a second class draws only its own lines here, never
  // the mage's.
  for (const modifier of classDef.modifierIds) {
    const level = build.modifiers[modifier] ?? 0;
    if (level < ARENA_MODIFIER_MAX_LEVEL) {
      pool.push({ kind: 'modifier', modifier, toLevel: level + 1 });
    } else if (classDef.keystones[modifier] && !build.keystones.has(modifier)) {
      // Keystones (PRD #378): a maxed, un-transcended modifier offers its one
      // binary transcendence - finishing a line is a beginning, not an end.
      pool.push({ kind: 'keystone', modifier });
    }
  }
  for (const stat of ARENA_STAT_IDS) {
    if (rules.excludedDraftStats.includes(stat)) continue;
    const level = build.stats[stat] ?? 0;
    if (level < ARENA_STAT_MAX_LEVEL) pool.push({ kind: 'stat', stat, toLevel: level + 1 });
  }
  // Ascensions (PRD #378): once every modifier, keystone, and stat is owned,
  // the pool never empties - both endless picks are always on the table, so
  // the ding beat keeps landing to the horn.
  if (pool.length === 0) {
    for (const ascension of ARENA_ASCENSION_IDS) {
      pool.push({ kind: 'ascension', ascension, count: (build.ascensions[ascension] ?? 0) + 1 });
    }
  }
  return pool;
}

/**
 * Roll one level-up draft: up to {@link ARENA_DRAFT_SIZE} **distinct** options
 * drawn from the pool with the run world's seeded rng (a partial Fisher-Yates,
 * so the same seed + same build always reproduces the identical offer). An
 * exhausted pool (everything owned and maxed) returns `[]` - the level lands
 * silently with no pause. Exported for the server slice's `PickArenaOption`
 * flow and for tests; {@link stepArena} calls it via the threshold check.
 */
export function rollArenaDraft(world: World, state: ArenaRunState): ArenaDraftOption[] {
  const pool = draftPool(state.build, state.classDef, state.challenge.rules);
  // The rare Flame Vortex offer (PRD #378): one seeded gate roll per draft, in
  // fixed order BEFORE the shuffle draws, so replays stay byte-identical.
  if (world.rng.next() < ARENA_VORTEX_OFFER_CHANCE) pool.push({ kind: 'vortex' });
  const count = Math.min(ARENA_DRAFT_SIZE, pool.length);
  for (let i = 0; i < count; i++) {
    const j = i + world.rng.nextInt(pool.length - i);
    const tmp = pool[i]!;
    pool[i] = pool[j]!;
    pool[j] = tmp;
  }
  return pool.slice(0, count);
}

/**
 * Consume any crossed XP thresholds. Each crossing raises the level and emits
 * a `level-up` beat; the first one with a non-empty draft pool rolls the offer
 * and pauses the run (one draft at a time - further banked thresholds resolve
 * after each pick, via {@link applyArenaPick}). Empty-pool levels land
 * silently and the loop continues.
 */
function checkLevelUps(world: World, state: ArenaRunState, beats: ArenaBeat[]): void {
  while (state.status === 'running' && state.xp >= arenaXpToNext(state.level)) {
    state.xp -= arenaXpToNext(state.level);
    state.level += 1;
    beats.push({ kind: 'level-up', level: state.level });
    const options = rollArenaDraft(world, state);
    if (options.length > 0) {
      state.pendingDraft = { level: state.level, options };
      pauseArenaForDraft(state); // exits the loop; banked XP waits for the pick
    }
  }
}

/**
 * Apply the runner's draft pick and resume the run - the resolver behind the
 * server slice's `PickArenaOption` command. Valid only while `paused-draft`
 * with a pending offer, an in-range `index`, and an option that still applies
 * to the build (a `weapon-level` for an unowned weapon is rejected - the offer
 * is rolled against the paused build so this cannot happen legitimately, but a
 * forged/stale pick must not consume the draft). Anything invalid returns
 * `null` and changes nothing - the pause stays until a valid pick. The pick
 * mutates the build observably:
 *
 *  - **modifier**: records the new modifier level (the next volley re-derives
 *    projectile count/kind/damage and the cascade from the table);
 *  - **stat**: records the new stat level; `mobility` re-derives the player
 *    mobile's gait from {@link ARENA_PLAYER_MS_PER_TILE} (never compounding),
 *    preserving position/facing/in-flight state, while `regen` takes effect
 *    passively (its level drives the per-tick heal in {@link stepArena}).
 *
 * Afterwards the run resumes - unless more banked XP immediately crosses the
 * next threshold, in which case the next draft is rolled and the run stays
 * paused (read `state.pendingDraft` after a pick: non-null means offer again).
 * A valid pick returns the beats it emitted - the `level-up` beat of each
 * banked threshold consumed here rather than inside {@link stepArena} - so the
 * server can broadcast the fanfare cues exactly like a step's beats.
 */
export function applyArenaPick(
  world: World,
  state: ArenaRunState,
  index: number,
): ArenaBeat[] | null {
  if (state.status !== 'paused-draft' || !state.pendingDraft) return null;
  const option = state.pendingDraft.options[index];
  if (!option) return null;
  if (option.kind === 'stat' && state.challenge.rules.excludedDraftStats.includes(option.stat)) {
    return null;
  }
  // Defensive: a pick must land exactly one level above the build's current
  // (the offer was rolled against the paused build so this cannot mismatch
  // legitimately, but a forged/stale pick must not consume the draft).
  if (option.kind === 'modifier') {
    const current = state.build.modifiers[option.modifier] ?? 0;
    if (option.toLevel !== current + 1 || option.toLevel > ARENA_MODIFIER_MAX_LEVEL) return null;
  }
  // A keystone pick must name a maxed, un-transcended modifier with a declared
  // keystone (same forged/stale-pick defense).
  if (option.kind === 'keystone') {
    if (!state.classDef.keystones[option.modifier]) return null;
    if ((state.build.modifiers[option.modifier] ?? 0) < ARENA_MODIFIER_MAX_LEVEL) return null;
    if (state.build.keystones.has(option.modifier)) return null;
  }
  // An ascension pick must land exactly one past the current count (unbounded).
  if (option.kind === 'ascension') {
    if (option.count !== (state.build.ascensions[option.ascension] ?? 0) + 1) return null;
  }

  // Beats this pick itself emits (a vortex summon's flash) plus the level-up
  // beats of any banked thresholds consumed below.
  const pickBeats: ArenaBeat[] = [];

  switch (option.kind) {
    case 'modifier':
      state.build.modifiers[option.modifier] = option.toLevel;
      // Heavy Draw (archer, ADR-0237): the first level swaps the held bow for a
      // crossbow (render-only), so the runner visibly draws the heavier weapon.
      if (state.classDef.id === 'archer' && option.modifier === 'heavy-draw') {
        const player = world.get(state.player);
        if (player) equipArcherWeapon(player, state.build.modifiers);
      }
      break;
    case 'keystone':
      // Binary: owning it is the whole effect - the next volley re-derives the
      // transformed behavior (pierce / fork / cataclysm / meteor) from the set.
      state.build.keystones.add(option.modifier);
      break;
    case 'vortex':
      // Immediate: the summon rises at the runner's feet as the run resumes.
      // Its ground-fx beat rides the returned beats for the server to enact.
      summonFlameVortex(world, state, pickBeats);
      break;
    case 'ascension':
      // Plain by design: the next volley re-derives damage/cooldown with the
      // grown count ({@link ./arenaWeapons.skillProjectileDamage} /
      // {@link ./arenaWeapons.skillCooldownTicks}).
      state.build.ascensions[option.ascension] = option.count;
      break;
    case 'stat': {
      state.build.stats[option.stat] = option.toLevel;
      const player = world.get(state.player);
      // `regen` needs no pick-time mutation: the per-tick passive heal in
      // stepArena reads its level off the build every interval (see below).
      if (option.stat === 'mobility' && player?.mobile) {
        // Rebuild the mobile with the re-derived gait; toState/fromState keeps
        // tile, facing, and any in-flight step/turn intact. The run gait
        // mirrors Mobile's run-is-half-walk rule (mobile.ts derives a default
        // msPerTileRun as msPerTile / 2), applied to the arena base so the
        // walk:run ratio the runner spawned with survives the draft.
        const s = player.mobile.toState();
        s.msPerTile = arenaMoveMsPerTile(ARENA_PLAYER_MS_PER_TILE, option.toLevel);
        s.msPerTileRun = arenaMoveMsPerTile(ARENA_PLAYER_MS_PER_TILE / 2, option.toLevel);
        player.mobile = Mobile.fromState(s);
      }
      break;
    }
  }

  state.pendingDraft = null;
  resumeArena(state);
  // Banked thresholds: a big kill (elite/boss) can cross several levels at
  // once - roll the next draft immediately, one pick at a time. The level-up
  // beats land in the returned array (there is no step to ride out of here).
  checkLevelUps(world, state, pickBeats);
  return pickBeats;
}

export function isArenaBuildAllowed(build: Pick<ArenaBuild, 'stats'>, rules: ArenaChallengeRules): boolean {
  return rules.excludedDraftStats.every((stat) => (build.stats[stat] ?? 0) === 0);
}

// ---------------------------------------------------------------------------
// Score & payout (Slice C, ADR-0175). Pure functions of the run tallies - the
// server slice feeds them into recordArenaRun / addGold at run end.
// ---------------------------------------------------------------------------

/** Score per enemy killed. */
export const ARENA_SCORE_PER_KILL = 10;

/** Score per full second survived (20 ticks). */
export const ARENA_SCORE_PER_SECOND = 1;

/** Score for one full boss health bar (scaled linearly; cumulative across recurring bosses). */
export const ARENA_SCORE_BOSS_BONUS = 500;

/** Ticks per second of sim time (the 50ms fixed step). */
const ARENA_TICKS_PER_SECOND = 20;

/**
 * A run's score: kills, survival time, and cumulative boss progress, each on its
 * declared weight - monotonic in all three, so the leaderboard rewards overall
 * performance (PRD #334 story 39). `bossValue` is the **cumulative** boss tally
 * (`bossKills + currentBossProgress`, ADR-0210): each defeated boss's full bar
 * plus the fraction stripped off the one currently alive - so it grows past 1 in
 * an endless run and is **not** clamped high (only floored at 0). Pure; the
 * durable best rides ADR-0172's `recordArenaRun`.
 */
export function arenaScore(kills: number, elapsedTicks: number, bossValue: number): number {
  const boss = Math.max(0, bossValue);
  return (
    kills * ARENA_SCORE_PER_KILL +
    Math.floor(elapsedTicks / ARENA_TICKS_PER_SECOND) * ARENA_SCORE_PER_SECOND +
    Math.round(boss * ARENA_SCORE_BOSS_BONUS)
  );
}

/** Final score points required to bank one gold (ADR-0407). */
export const ARENA_PAYOUT_POINTS_PER_GOLD = 10;

/**
 * Gold an Arena run banks, derived directly from the final leaderboard score.
 * This keeps reward ordering identical to score ordering and gives players one
 * simple exchange rate: 30,000 points bank 3,000 gold.
 */
export function arenaPayout(score: number): number {
  return Math.floor(Math.max(0, score) / ARENA_PAYOUT_POINTS_PER_GOLD);
}

/**
 * The bank-herald line announcing a new all-time Arena high score (ADR-0210) -
 * pure and world-free (the {@link stormFinaleLine} pattern), so it is unit-tested
 * out of the socket layer. Broadcast by the server on the town-only herald path
 * beside the {@link ../net/protocol.ArenaRecordMessage} victory-sting broadcast.
 */
export function arenaRecordHeraldLine(name: string, score: number): string {
  return `${name} has conquered the Arena with a new high score of ${score}!`;
}
